import { api, ApiError, downloadPost } from './client';
import { xhrUpload } from './xhrUpload';
import { czytajSse, json, strumienZWynikiem } from './sse';
import type { UploadProgress } from '@/lib/uploadLimits';
import type {
  AuditEntry, DashboardData, DashboardLayoutItem, ArchiveDocument, AiDraft, Client,
  StudyDetail, StudySummary, AuthorityProposal,
  VariantRecommendation, StudyType, Intake, User, MapLayer, StudyTypeRequiredLayer, Task, TaskInput,
  Offer, OfferStatus, OfferType, OfferDeadlineMode, OfferDocument, Company, OfferTemplate, WorkType, AiGeneratedOffer,
  IntakeOperatData, BoreholeOperatData, ProtectedAreaReference, ProtectedAreaType,
  WaterUsePurpose, ComplianceDeadline, ComplianceCategory, ComplianceStatus,
  Employee, EmployeeInput, EmployeeMeta, EmploymentContract, ContractInput,
  EmployeeQualification, QualificationInput, EmploymentStatus,
  Timesheet, WorkTimeEntry, WorkTimeEntryInput, MonthlySettlement, SettlementInput,
  EmployeePayroll, EmployeePayrollInput,
  Vehicle, VehicleInput, VehicleMeta, VehicleService, VehicleServiceInput, VehicleStatus,
  VehicleOdometerReading, VehicleOdometerReadingInput, DocumentCategory,
  Trip, TripInput, TripMeta, Brigade, BrigadeInput,
  LeaveRequest, LeaveInput, LeaveOverview, LeaveBalance, EntitlementInput, LeaveMeta,
  Invoice, InvoiceDetail, InvoiceDirection, InvoiceMeta, PaymentStatusInput, BankTransaction, BankTxMatchStatus, BankMeta,
  ManualInvoiceInput, Vendor, VendorInput, Subscription, SubscriptionInput, SubscriptionDetail,
  SubscriptionPeriod, SubscriptionMeta, SubscriptionStatusCode, SubscriptionCategoryCode, InvoiceCandidate,
  DwutygodniowkaExtraction, DwuConfirmRow, BulkExtractResult
} from '@/types';
import type { BoreholeCardModel, WaterQualityParameterDto, CardExtraction } from '@/components/card/cardTypes';

export const auth = {
  login: (username: string, password: string, totpCode?: string) =>
    api.post<User>('/api/auth/login',
      totpCode ? { username, password, totpCode } : { username, password }),
  logout: () => api.post<{ ok: boolean }>('/api/auth/logout'),
  me: () => api.get<SessionResponse | User>('/api/auth/me'),
  changePassword: (starePassword: string | null, nowePassword: string) =>
    api.post<{ ok: boolean }>('/api/auth/change-password', { starePassword, nowePassword }),
  updateProfile: (data: { firstName: string; lastName: string; email?: string | null }) =>
    api.put<User>('/api/auth/me/profile', data),
};

/** Stan trybu „pracuj jako" — null przy zwykłej sesji. */
export interface ImpersonationState {
  adminLogin: string;
  adminFullName: string;
  since: string | null;
}

export interface SessionResponse {
  user: User;
  passwordChangeRequired: boolean;
  impersonation: ImpersonationState | null;
}

/** Kandydat do wcielenia się — backend liczy `dozwolone` z macierzy uprawnień, front nie zgaduje. */
export interface ImpersonationCandidate {
  id: number;
  login: string;
  fullName: string;
  dozwolone: boolean;
  powodBlokady: string | null;
}

export const impersonation = {
  candidates: () => api.get<ImpersonationCandidate[]>('/api/auth/impersonate/candidates'),
  /** @param password hasło ZALOGOWANEGO administratora (potwierdzenie tożsamości). */
  start: (userId: number, password: string) =>
    api.post<SessionResponse>('/api/auth/impersonate', { userId, password }),
  stop: () => api.post<SessionResponse>('/api/auth/impersonate/stop'),
};

export interface NotificationPrefDto {
  category: string;
  displayName: string;
  description: string;
  inAppEnabled: boolean;
  emailEnabled: boolean;
  defaultInApp: boolean;
  defaultEmail: boolean;
}

export const notificationPrefs = {
  list: () => api.get<NotificationPrefDto[]>('/api/auth/me/notification-preferences'),
  update: (category: string, body: { inAppEnabled?: boolean; emailEnabled?: boolean }) =>
    api.put<NotificationPrefDto>(`/api/auth/me/notification-preferences/${category}`, body),
  reset: (category: string) =>
    api.del<{ ok: boolean; category: string; defaultInApp: boolean; defaultEmail: boolean }>(
      `/api/notifications/me/preferences/${category}`),
};

export interface NotificationDto {
  id: number;
  category: string;
  title: string;
  body: string | null;
  link: string | null;
  relatedEntityType: string | null;
  relatedEntityId: number | null;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationsInbox {
  unreadCount: number;
  notifications: NotificationDto[];
}

export const notifications = {
  inbox: (limit = 50) => api.get<NotificationsInbox>(`/api/notifications/me?limit=${limit}`),
  markRead: (id: number) => api.post<NotificationDto>(`/api/notifications/${id}/read`),
  markAllRead: () => api.post<{ ok: boolean; marked: number }>('/api/notifications/me/read-all'),
  remove: (id: number) => api.del<{ ok: boolean }>(`/api/notifications/${id}`),
};

export const dashboard = {
  get: () => api.get<DashboardData>('/api/dashboard'),
};

export interface DashboardPrefResponse { layout: DashboardLayoutItem[] | null; }

/** Układ widgetów dashboardu per użytkownik (V78). */
export const dashboardPrefs = {
  get: () => api.get<DashboardPrefResponse>('/api/auth/me/dashboard-preferences'),
  put: (layout: DashboardLayoutItem[]) =>
    api.put<DashboardPrefResponse>('/api/auth/me/dashboard-preferences', { layout }),
};

/** Kolejność menu głównego (id sekcji i pozycji z NAV). null = kolejność domyślna. */
export interface NavPrefResponse { order: string[] | null; }

export const navPrefs = {
  get: () => api.get<NavPrefResponse>('/api/auth/me/nav-preferences'),
  /** Pusta lista = powrót do kolejności domyślnej. */
  put: (order: string[]) => api.put<NavPrefResponse>('/api/auth/me/nav-preferences', { order }),
};

export interface SearchHit {
  type: 'client' | 'intake' | 'study';
  id: number;
  title: string;
  subtitle: string;
}

export interface SearchResult {
  clients: SearchHit[];
  intakes: SearchHit[];
  studies: SearchHit[];
}

export const search = {
  global: (q: string) => api.get<SearchResult>('/api/search?q=' + encodeURIComponent(q)),
};

export interface BoreholeHousingInput {
  /** Id typu obudowy ze słownika /api/borehole-housing-types. null = brak typu. */
  typeId?: number | null;
  diameterMm?: number | null;
  depth?: number | null;
  cover?: string | null;
  steelLadderDiameterMm?: number | null;
  hydraulicFittings?: string | null;
}

export interface BoreholeInput {
  designation: string;
  function: string;
  status?: string;
  totalDepth?: number;
  diameterMm?: number;
  yieldM3h?: number;
  latitude?: number;
  longitude?: number;
  yearCompleted?: number;
  remarks?: string;
  /** Średniodobowy pobór ze studni w m³/dobę (×365 daje roczny pobór ujęcia). */
  avgDailyIntakeM3?: number | null;
  /** Sekcja obudowy — gdy != undefined nadpisuje wszystkie pola obudowy (null czyści). */
  housing?: BoreholeHousingInput;
  /** Sekcja danych do operatu wodnoprawnego (Faza 3) — null czyści pole. */
  operat?: BoreholeOperatData;
}

export interface BoreholeHousingType {
  id: number;
  code: string;
  name: string;
  description: string | null;
  sortOrder: number;
  active: boolean;
}

export interface BoreholeHousingTypeInput {
  code: string;
  name: string;
  description?: string;
  sortOrder?: number;
  active?: boolean;
}

export const housingTypes = {
  list: (includeInactive?: boolean) =>
    api.get<BoreholeHousingType[]>(
      '/api/borehole-housing-types' + (includeInactive ? '?includeInactive=true' : ''),
    ),
  create: (data: BoreholeHousingTypeInput) =>
    api.post<BoreholeHousingType>('/api/borehole-housing-types', data),
  update: (id: number, data: Partial<BoreholeHousingTypeInput>) =>
    api.put<BoreholeHousingType>(`/api/borehole-housing-types/${id}`, data),
  delete: (id: number) =>
    api.del<{ ok: boolean; deactivated: boolean }>(`/api/borehole-housing-types/${id}`),
};

export interface BoreholeLayerInput {
  fromDepth: number;
  toDepth: number;
  symbol: string;
  description: string;
  color?: string;
  pattern?: string;
  lithologyTypeId?: number;
  remarks?: string;
}

export interface LithologyType {
  id: number;
  code: string;
  name: string;
  description: string | null;
  color: string;
  pattern: string;
  sortOrder: number;
  active: boolean;
}

export interface LithologyTypeInput {
  code: string;
  name: string;
  description?: string;
  color: string;
  pattern: string;
  sortOrder?: number;
  active?: boolean;
}

export const lithology = {
  list: (includeInactive?: boolean) =>
    api.get<LithologyType[]>('/api/lithology-types' + (includeInactive ? '?includeInactive=true' : '')),
  create: (data: LithologyTypeInput) => api.post<LithologyType>('/api/lithology-types', data),
  update: (id: number, data: Partial<LithologyTypeInput>) =>
    api.put<LithologyType>(`/api/lithology-types/${id}`, data),
  delete: (id: number) => api.del<{ ok: boolean; deactivated: boolean }>(`/api/lithology-types/${id}`),
};

/** Podsumowanie otworu na globalnej liście modułu „Karty otworów" (wszystkie ujęcia). */
export interface BoreholeSummary {
  id: number;
  designation: string;
  function: string;
  funkcjaName: string;
  funkcjaAbbreviation: string;
  status: string;
  totalDepth: number | null;
  intakeId: number | null;
  intakeName: string | null;
  intakeNumber: string | null;
  clientName: string | null;
  town: string | null;
  voivodeship: string | null;
  hasCard: boolean;
}

export const boreholes = {
  /** Wszystkie otwory ze wszystkich ujęć (najnowsze na górze) — moduł „Karty otworów". */
  listAll: () => api.get<BoreholeSummary[]>('/api/boreholes'),
  listForIntake: (intakeId: number) =>
    api.get<{ id: number; designation: string; function: string }[]>(`/api/intakes/${intakeId}/boreholes`),
  create: (intakeId: number, data: BoreholeInput) =>
    api.post<{ id: number }>(`/api/intakes/${intakeId}/boreholes`, data),
  update: (id: number, data: Partial<BoreholeInput>) =>
    api.put<unknown>(`/api/boreholes/${id}`, data),
  delete: (id: number) => api.del<{ ok: boolean }>(`/api/boreholes/${id}`),

  listLayers: (boreholeId: number) =>
    api.get<unknown[]>(`/api/boreholes/${boreholeId}/layers`),
  createLayer: (boreholeId: number, data: BoreholeLayerInput) =>
    api.post<unknown>(`/api/boreholes/${boreholeId}/layers`, data),
  updateLayer: (id: number, data: Partial<BoreholeLayerInput>) =>
    api.put<unknown>(`/api/borehole-layers/${id}`, data),
  deleteLayer: (id: number) => api.del<{ ok: boolean }>(`/api/borehole-layers/${id}`),

  /** URL do karty otworu (PDF) — do otwarcia/pobrania w nowym oknie. */
  kartaUrl: (id: number) => `/api/boreholes/${id}/karta`,
  /** Generuje kartę otworu (PDF) i zapisuje w archiwum systemu (powiązaną z ujęciem). */
  zapiszKarta: (id: number) =>
    api.post<{ documentId: number; name: string }>(`/api/boreholes/${id}/karta/zapisz`),
};

/** Nowa „Karta otworu" (Zestawienie wyników wiercenia) — model dla edytora wizualnego + render SVG→PDF. */
export const boreholeCard = {
  get: (id: number) => api.get<BoreholeCardModel>(`/api/boreholes/${id}/card`),
  save: (id: number, data: unknown) => api.put<BoreholeCardModel>(`/api/boreholes/${id}/card`, data),
  /** Zapisuje wyrenderowany SVG jako wektorowy PDF w archiwum systemu. */
  savePdf: (id: number, svg: string) =>
    api.post<{ documentId: number; name: string }>(`/api/boreholes/${id}/karta/svg/zapisz`, { svg }),
  /** Transkoduje SVG → PDF i zwraca Blob do podglądu/pobrania. */
  renderPdf: async (id: number, svg: string): Promise<Blob> => {
    const res = await fetch(`/api/boreholes/${id}/karta/svg`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ svg }),
    });
    if (!res.ok) throw new ApiError(res.status, res.statusText);
    return res.blob();
  },
  /** Miniatura mapy lokalizacji otworu jako data URI (PNG) — osadzana w SVG karty. null gdy brak. */
  mapThumbnail: async (id: number, scale?: string | null): Promise<string | null> => {
    const q = scale ? `?scale=${encodeURIComponent(scale)}` : '';
    const res = await fetch(`/api/boreholes/${id}/map-thumbnail${q}`, { credentials: 'include' });
    if (res.status === 204 || !res.ok) return null;
    const blob = await res.blob();
    return new Promise<string>((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result as string);
      r.readAsDataURL(blob);
    });
  },
  /** Prefill AI — wyciąga dane karty z wgranego dokumentu (propozycja do scalenia). */
  extract: (id: number, file: File, onProgress?: (p: UploadProgress) => void): Promise<CardExtraction> => {
    const fd = new FormData();
    fd.append('file', file);
    return xhrUpload<CardExtraction>(`/api/boreholes/${id}/card/extract`, fd, { onProgress });
  },
  /** Pobiera obraz (np. logo firmy z /api/companies/{id}/logo) jako data URI do osadzenia w SVG. */
  imageDataUri: async (url: string): Promise<string | null> => {
    const res = await fetch(url, { credentials: 'include' });
    if (res.status === 204 || !res.ok) return null;
    const blob = await res.blob();
    return new Promise<string>((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result as string);
      r.readAsDataURL(blob);
    });
  },
};

export const waterQualityParameters = {
  list: () => api.get<WaterQualityParameterDto[]>('/api/water-quality-parameters'),
};

export interface PshBoreholeProps {
  idCbdg: string | null;
  name: string | null;
  depthM: number | null;
  year: number | null;
  purpose: string | null;
  town: string | null;
  gmina: string | null;
  powiat: string | null;
  cardUrl: string | null;
  source: string;
}

export const psh = {
  /** GeoJSON FeatureCollection otworów PSH (CBDG) w bbox="minLon,minLat,maxLon,maxLat". */
  boreholes: (bbox: string) =>
    api.get<{ type: 'FeatureCollection'; features: unknown[]; truncated: boolean }>(
      `/api/psh/boreholes?bbox=${encodeURIComponent(bbox)}`),
};

/** Operat update — wszystkie pola opcjonalne; null = wyczyść; brak pola = pomiń (server set null). */
export interface IntakeOperatInput {
  jcwpdCode?: string | null;
  jcwpdName?: string | null;
  regionWodny?: string | null;
  dorzecze?: string | null;
  waterUsePurpose?: WaterUsePurpose | null;
  waterUseDescription?: string | null;
  waterRecipients?: string | null;
  requestedPermitYears?: number | null;
  approvedResourceM3h?: number | null;
  approvedResourceDecisionRef?: string | null;
  approvedResourceDecisionDate?: string | null;
  approvedResourceDecisionAuthority?: string | null;
  previousPermitRef?: string | null;
  previousPermitDate?: string | null;
  previousPermitAuthority?: string | null;
  previousPermitExpiry?: string | null;
  treatmentDescription?: string | null;
  distributionDescription?: string | null;
  inFloodHazardArea?: boolean | null;
  inGzwp?: boolean | null;
  gzwpNumber?: number | null;
  gzwpName?: string | null;
}

export interface SichardtResult {
  radiusM: number;
  maxDrawdownM: number;
  avgPermeabilityKMs: number;
  boreholesUsed: number;
  method: string;
}

export interface ProtectedAreaInput {
  type: ProtectedAreaType;
  name: string;
  code?: string | null;
  distanceM?: number | null;
  objectOfProtection?: string | null;
  impactAssessment?: string | null;
}

export interface EnumOption { code: string; displayName: string; }

export const intakes = {
  list: (filter?: string, authority?: string) => {
    const p = new URLSearchParams();
    if (filter) p.set('filter', filter);
    if (authority) p.set('authority', authority);
    const qs = p.toString();
    return api.get<Intake[]>('/api/intakes' + (qs ? '?' + qs : ''));
  },
  get: (id: number) => api.get<Intake>(`/api/intakes/${id}`),
  stats: () => api.get<Record<string, number>>('/api/intakes/stats'),

  /** Czy ten klient ma już ujęcie o takiej nazwie — podpowiedź w kreatorze. Zapis blokuje serwer. */
  nameCheck: (clientId: number, name: string) =>
    api.get<{ taken: boolean; intakeId: number | null; number: string | null }>(
      `/api/intakes/name-check?clientId=${clientId}&name=${encodeURIComponent(name)}`),
  create: (data: {
    name: string;
    clientId: number;
    /** Wydajność Q [m³/h] — opcjonalna (może być nieznana na etapie planowania). */
    q?: number;
    town?: string;
    county?: string;
    voivodeship?: string;
    gmina?: string;
    latitude?: number;
    longitude?: number;
    decydentOverride?: string;
    justification?: string;
    zarzadZlewniId?: number | null;
    kind?: 'NEW' | 'EXISTING';
    otworyPlanowane?: { designation: string; function: string; depth?: number;
                        latitude?: number; longitude?: number }[];
  }) => api.post<Intake>('/api/intakes', data),
  setZarzadZlewni: (id: number, zarzadZlewniId: number | null) =>
    api.put<Intake>(`/api/intakes/${id}/zarzad-zlewni`, { zarzadZlewniId }),

  /** Porządkowanie ujęcia-studni: nazwa całego ujęcia, studnie, dołączenie innych ujęć klienta (do archiwum). Wymaga INTAKE_EDIT. */
  merge: (id: number, data: {
    name: string;
    sourceIds: number[];
    wells: { intakeId: number; designation: string; function?: 'PRODUCTION' | 'REPLACEMENT' }[];
  }) => api.post<Intake>(`/api/intakes/${id}/merge`, data),

  /** Zmiana rodzaju ujęcia (nowe/istniejące) po utworzeniu. Wymaga INTAKE_EDIT. */
  setKind: (id: number, kind: 'NEW' | 'EXISTING') =>
    api.put<Intake>(`/api/intakes/${id}/kind`, { kind }),

  /** Aktualizacja lokalizacji ujęcia (współrzędne + pełny adres) po utworzeniu. Współrzędne
   *  opcjonalne; gdy podane, backend waliduje zakres PL (lat 48-56 / lng 13-25). Wymaga INTAKE_EDIT. */
  updateLocation: (id: number, data: {
    latitude?: number | null;
    longitude?: number | null;
    town?: string | null;
    county?: string | null;
    voivodeship?: string | null;
    gmina?: string | null;
  }) => api.put<Intake>(`/api/intakes/${id}/location`, data),

  /** AI wyciąga dane ujęcia + otworów z wgranego dokumentu (operat, dokumentacja,
   *  decyzja). Sam endpoint NIE tworzy ujęcia — zwraca pre-fill dla kreatora. */
  extractFromDocument: (file: File, onProgress?: (p: UploadProgress) => void): Promise<ExtractedIntakeData> => {
    const fd = new FormData();
    fd.append('file', file);
    return xhrUpload<ExtractedIntakeData>('/api/intakes/extract-from-document', fd, { onProgress });
  },

  /** Faza 3 — zapisz dane do operatu wodnoprawnego (JCWPd, zasoby, GZWP, itd.). */
  updateOperat: (id: number, data: IntakeOperatInput) =>
    api.put<Intake>(`/api/intakes/${id}/operat`, data),

  /** Wywołaj obliczenie R wzorem Sichardta — wymaga s i k dla studni. */
  calcSichardt: (id: number) =>
    api.post<SichardtResult>(`/api/intakes/${id}/radius/sichardt`),

  /** Ręczne ustawienie R — source = "MANUAL" lub "DOKUMENTACJA". */
  setRadiusManual: (id: number, radiusM: number, source: string, comment?: string) =>
    api.put<Intake>(`/api/intakes/${id}/radius/manual`, { radiusM, source, comment }),

  /** Słownik celów korzystania z wód. */
  waterUsePurposes: () => api.get<EnumOption[]>('/api/intakes/water-use-purposes'),

  /** R&D F-INT — JCWPd z hydroportalu PGW WP. */
  jcwpdLookup: (id: number) =>
    api.post<JcwpdLookupResponse>(`/api/intakes/${id}/jcwpd-lookup`),

  /** R&D F-INT — sprawdzenie zagrożenia powodziowego z ISOK MZP. */
  floodCheck: (id: number) =>
    api.post<FloodCheckResponse>(`/api/intakes/${id}/flood-check`),

  /** R&D F-INT — auto-detekcja form ochrony przyrody w promieniu R z GDOŚ. */
  autoDetectProtectedAreas: (id: number) =>
    api.post<AutoDetectResponse>(`/api/intakes/${id}/protected-areas/auto-detect`),
};

export interface JcwpdLookupResponse {
  ok: boolean;
  reason?: string;
  jcwpdCode?: string;
  jcwpdName?: string;
  regionWodny?: string;
  dorzecze?: string;
  dataVersion?: string;
  fetchedAt?: string;
  fromCache?: boolean;
  source?: string;
}

export interface FloodCheckResponse {
  ok: boolean;
  reason?: string;
  inFloodHazardArea?: boolean;
  q10?: boolean;
  q1?: boolean;
  q02?: boolean;
  fetchedAt?: string;
  fromCache?: boolean;
  source?: string;
}

export interface AutoDetectResponse {
  ok: boolean;
  foundTotal: number;
  created: number;
  updated: number;
  keptManual: number;
  layersQueried: number;
  radiusM: number;
  fetchedAt: string;
}

export const protectedAreas = {
  /** Słownik typów form ochrony (dla selectów). */
  types: () => api.get<EnumOption[]>('/api/protected-area-types'),
  list: (intakeId: number) =>
    api.get<ProtectedAreaReference[]>(`/api/intakes/${intakeId}/protected-areas`),
  add: (intakeId: number, data: ProtectedAreaInput) =>
    api.post<ProtectedAreaReference>(`/api/intakes/${intakeId}/protected-areas`, data),
  update: (id: number, data: ProtectedAreaInput) =>
    api.put<ProtectedAreaReference>(`/api/protected-areas/${id}`, data),
  remove: (id: number) => api.del<{ ok: boolean }>(`/api/protected-areas/${id}`),
};

export interface ZarzadZlewniDto {
  id: number;
  code: string;
  name: string;
  rzgwCode: string;
  rzgwName: string;
  addressLine1: string | null;
  addressPostalCode: string | null;
  addressCity: string | null;
  voivodeshipHints: string | null;
  phone: string | null;
  email: string | null;
  epuapAddress: string | null;
  edoreczeniaAddress: string | null;
  notes: string | null;
  /** Opcjonalny dodatkowy prompt doklejany do AiService.budujPrompt dla opracowań pod tym ZZ. */
  customAiPrompt: string | null;
  intakesCount: number | null;
  studiesCount: number | null;
  rejectionsCount: number | null;
  /** Liczba ZAKOŃCZONYCH opracowań pod tym ZZ — baza dla statystyki avgDecisionDays. */
  completedStudiesCount: number | null;
  /** Średni czas (w dniach) od pierwszej wysyłki do akceptacji urzędu. Null gdy brak danych. */
  avgDecisionDays: number | null;
}

export interface ZarzadZlewniByCoordsResult {
  match: ZarzadZlewniDto | null;
  candidates: ZarzadZlewniDto[];
  reason: string;
}

export interface ZarzadStudyRef {
  id: number;
  number: string;
  title: string;
  typeAbbreviation: string;
  typeCode: string;
  statusName: string;
  statusColor: string;
  intakeName: string | null;
  clientName: string | null;
  rejectionsCount: number;
  createdAt: string;
}

export interface ZarzadRankingEntry {
  id: number;
  name: string;
  secondary: string | null;
  count: number;
}

export interface ZarzadStatsResponse {
  topRejectingZz: ZarzadRankingEntry[];
  mostRejectedStudies: ZarzadRankingEntry[];
  totalRejections: number;
  totalUnresolved: number;
}

export const zarzadyZlewni = {
  list: () => api.get<ZarzadZlewniDto[]>('/api/zarzady-zlewni'),
  detail: (id: number) => api.get<ZarzadZlewniDto>(`/api/zarzady-zlewni/${id}`),
  studies: (id: number) => api.get<ZarzadStudyRef[]>(`/api/zarzady-zlewni/${id}/studies`),
  updateNotes: (id: number, notes: string) =>
    api.put<ZarzadZlewniDto>(`/api/zarzady-zlewni/${id}/notes`, { notes }),
  updateCustomAiPrompt: (id: number, customAiPrompt: string | null) =>
    api.put<ZarzadZlewniDto>(`/api/zarzady-zlewni/${id}/custom-ai-prompt`, { customAiPrompt }),
  stats: () => api.get<ZarzadStatsResponse>('/api/zarzady-zlewni/stats'),
  byCoords: (lat: number, lon: number, voivodeship?: string) => {
    const p = new URLSearchParams({ lat: String(lat), lon: String(lon) });
    if (voivodeship) p.set('voivodeship', voivodeship);
    return api.get<ZarzadZlewniByCoordsResult>('/api/zarzady-zlewni/by-coords?' + p.toString());
  },
};

export interface StudyRejectionDto {
  id: number;
  rejectedAt: string;
  reason: string | null;
  resolvedAt: string | null;
  zarzadZlewniId: number | null;
  zarzadZlewniName: string | null;
  createdByName: string | null;
}

export const studyRejections = {
  list: (studyId: number) => api.get<StudyRejectionDto[]>(`/api/studies/${studyId}/rejections`),
  create: (studyId: number, reason: string) =>
    api.post<StudyRejectionDto>(`/api/studies/${studyId}/rejections`, { reason }),
  resolve: (rejectionId: number) =>
    api.post<StudyRejectionDto>(`/api/studies/rejections/${rejectionId}/resolve`),
  remove: (rejectionId: number) =>
    api.del<{ ok: boolean }>(`/api/studies/rejections/${rejectionId}`),
};

export interface ClientInput {
  name: string;
  nip?: string;
  phone?: string;
  email?: string;
  addressLine1?: string;
  city?: string;
  postalCode?: string;
  voivodeship?: string;
  county?: string;
  gmina?: string;
  /** Współrzędne WGS84 — backend odrzuci podanie tylko jednej z pary. */
  latitude?: number | null;
  longitude?: number | null;
  locationNote?: string | null;
  remarks?: string;
}

export interface GeoReverseResult {
  voivodeship: string | null;
  county: string | null;
  gmina: string | null;
  obreb: string | null;
  parcelNumber: string | null;
  teryt: string | null;
  source: string | null;
  /** Miejscowość (geokoder GUGiK; fallback: nazwa obrębu). */
  town: string | null;
  /** Identyfikator działki EGiB (do zapisu jako działka ujęcia). */
  egibId: string | null;
  /** Geometria działki (GeoJSON Polygon/MultiPolygon, lon-lat WGS84) — do zapisu / mapy. */
  geometry: { type: 'Polygon' | 'MultiPolygon'; coordinates: unknown } | null;
}

/** Podpowiedź miejscowości z wyszukiwarki na mapie (GUGiK PRNG, prefiks). */
export interface Locality {
  city: string;
  gmina: string;
  county: string;
  voivodeship: string;
  lat: number;
  lng: number;
  teryt?: string;
  simc?: string;
  type?: string; // rodzaj obiektu (miasto / wieś / osada …)
}

export const geo = {
  /** Odwrotne geokodowanie po współrzędnych (WGS84) → województwo/powiat/gmina (GUGiK ULDK).
   *  Zwraca null gdy w punkcie nie ma działki (backend 204). */
  reverse: async (lat: number, lng: number): Promise<GeoReverseResult | null> => {
    const res = await fetch(`/api/geo/reverse?lat=${lat}&lng=${lng}`, { credentials: 'include' });
    if (res.status === 204) return null;
    if (!res.ok) throw new ApiError(res.status, 'Reverse geocode failed');
    return await res.json() as GeoReverseResult;
  },
  /** Podpowiedzi miejscowości (min. 3 znaki) → lista z gminą/powiatem + współrzędne. */
  localities: (q: string) => api.get<Locality[]>(`/api/geo/localities?q=${encodeURIComponent(q)}`),
};

export const clients = {
  list: (search?: string) => {
    const qs = search && search.trim() ? '?search=' + encodeURIComponent(search.trim()) : '';
    return api.get<Client[]>('/api/clients' + qs);
  },
  get: (id: number) => api.get<Client>(`/api/clients/${id}`),
  create: (data: ClientInput) => api.post<Client>('/api/clients', data),
  update: (id: number, data: ClientInput) => api.put<Client>(`/api/clients/${id}`, data),
};

// ── Relacje z klientem: profil, osoby kontaktowe, historia kontaktów (V181) ──────────────

export interface ClientOption { code: string; label: string }

export interface ClientOptions {
  types: ClientOption[];
  statuses: ClientOption[];
  contactRoles: ClientOption[];
  activityKinds: ClientOption[];
  /** code = id użytkownika jako tekst */
  managers: ClientOption[];
}

export interface ClientProfile {
  clientId: number;
  shortName: string | null;
  clientType: string | null;
  clientTypeName: string | null;
  clientStatus: string | null;
  clientStatusName: string | null;
  regon: string | null;
  krs: string | null;
  website: string | null;
  invoiceEmail: string | null;
  correspondenceAddress: string | null;
  bankAccount: string | null;
  accountManagerId: number | null;
  accountManagerName: string | null;
}

export type ClientProfileInput = Omit<ClientProfile, 'clientId' | 'clientTypeName' | 'clientStatusName' | 'accountManagerName'>;

export interface ClientContact {
  id: number;
  firstName: string | null;
  lastName: string;
  fullName: string;
  position: string | null;
  department: string | null;
  phone: string | null;
  phone2: string | null;
  email: string | null;
  email2: string | null;
  roles: ClientOption[];
  primary: boolean;
  active: boolean;
  notes: string | null;
}

export interface ClientContactInput {
  firstName: string | null;
  lastName: string;
  position: string | null;
  department: string | null;
  phone: string | null;
  phone2: string | null;
  email: string | null;
  email2: string | null;
  roles: string[];
  primary: boolean;
  active: boolean;
  notes: string | null;
}

export interface ClientActivity {
  id: number;
  kind: string;
  kindName: string;
  activityAt: string;
  contactId: number | null;
  contactName: string | null;
  summary: string;
  details: string | null;
  nextStep: string | null;
  nextStepDate: string | null;
  nextStepDone: boolean;
  createdByName: string | null;
}

export interface ClientActivityInput {
  kind: string;
  activityAt: string | null;
  contactId: number | null;
  summary: string;
  details: string | null;
  nextStep: string | null;
  nextStepDate: string | null;
}

export const clientRelations = {
  options: () => api.get<ClientOptions>('/api/client-options'),
  profile: (id: number) => api.get<ClientProfile>(`/api/clients/${id}/profile`),
  saveProfile: (id: number, data: ClientProfileInput) => api.put<ClientProfile>(`/api/clients/${id}/profile`, data),
  contacts: (id: number) => api.get<ClientContact[]>(`/api/clients/${id}/contacts`),
  createContact: (id: number, data: ClientContactInput) => api.post<ClientContact>(`/api/clients/${id}/contacts`, data),
  updateContact: (id: number, cid: number, data: ClientContactInput) =>
    api.put<ClientContact>(`/api/clients/${id}/contacts/${cid}`, data),
  deleteContact: (id: number, cid: number) => api.del<void>(`/api/clients/${id}/contacts/${cid}`),
  activities: (id: number) => api.get<ClientActivity[]>(`/api/clients/${id}/activities`),
  createActivity: (id: number, data: ClientActivityInput) =>
    api.post<ClientActivity>(`/api/clients/${id}/activities`, data),
  updateActivity: (id: number, aid: number, data: ClientActivityInput) =>
    api.put<ClientActivity>(`/api/clients/${id}/activities/${aid}`, data),
  markDone: (id: number, aid: number, done: boolean) =>
    api.post<ClientActivity>(`/api/clients/${id}/activities/${aid}/done`, { done }),
  deleteActivity: (id: number, aid: number) => api.del<void>(`/api/clients/${id}/activities/${aid}`),
};

export interface CompletenessIssue {
  severity: 'ERROR' | 'WARNING' | 'INFO';
  code: string;
  message: string;
  hint: string | null;
  /** Oryginalna severity przed nadpisaniem (gdy issue jest overridden). */
  originalSeverity?: 'ERROR' | 'WARNING' | 'INFO' | null;
  overridden?: boolean;
  overrideAction?: ValidationOverrideAction | null;
  aiPromptHint?: string | null;
  overrideNote?: string | null;
}

export interface CompletenessReport {
  errors: number;
  warnings: number;
  infos: number;
  canProceedToSign: boolean;
  issues: CompletenessIssue[];
}

/** Override walidacji — geolog ignoruje błąd albo dopisuje wskazówkę dla AI. */
export type ValidationOverrideAction = 'IGNORE' | 'AI_HINT';

export interface ValidationOverride {
  id: number;
  code: string;
  action: ValidationOverrideAction;
  aiPrompt: string | null;
  note: string | null;
  setByLogin: string | null;
  setByName: string | null;
  createdAt: string | null;
}

export const studies = {
  list: (filter?: string, type?: string, clientId?: number) => {
    const p = new URLSearchParams();
    if (filter) p.set('filter', filter);
    if (type) p.set('type', type);
    if (clientId) p.set('clientId', String(clientId));
    const qs = p.toString();
    return api.get<StudySummary[]>('/api/studies' + (qs ? '?' + qs : ''));
  },
  get: (id: number) => api.get<StudyDetail>(`/api/studies/${id}`),
  /** Tworzy opracowanie poza klasyczną ścieżką oferta→umowa/zlecenie.
   *  Wymaga STUDY_CREATE. Study powstaje z contract=null. */
  create: (data: {
    intakeId: number;
    type: string;
    title: string;
    deadline?: string | null;
    budget?: number | null;
    priority?: string | null;
    managerId?: number | null;
    geologistId?: number | null;
  }) => api.post<StudyDetail>('/api/studies', data),
  /** Lekki wpis historyczny istniejącego opracowania — tylko pod pilnowanie terminu (bez workflow). */
  createHistorical: (data: {
    intakeId: number;
    type: string;
    title: string;
    status: string;
    startDate?: string | null;
    deadline?: string | null;
    completedDate?: string | null;
    managerId?: number | null;
    geologistId?: number | null;
    budget?: number | null;
  }) => api.post<StudyDetail>('/api/studies/historical', data),
  drafts: (id: number) => api.get<AiDraft[]>(`/api/studies/${id}/drafts`),
  types: () => api.get<StudyType[]>('/api/studies/types'),
  setDispatchDate: (id: number, date: string | null) =>
    api.put<StudySummary>(`/api/studies/${id}/dispatch-date`, { date }),
  runAiVerification: (id: number) =>
    api.post<AiVerificationReport>(`/api/studies/${id}/ai-verification`),
  lastAiVerification: (id: number) =>
    api.get<AiVerificationReport | null>(`/api/studies/${id}/ai-verification`),
  acceptVerificationIssue: (id: number, idx: number) =>
    api.post<{ report: AiVerificationReport; applied: boolean }>(
      `/api/studies/${id}/ai-verification/issues/${idx}/accept`),
  rejectVerificationIssue: (id: number, idx: number) =>
    api.post<AiVerificationReport>(
      `/api/studies/${id}/ai-verification/issues/${idx}/reject`),
  completenessCheck: (id: number) =>
    api.get<CompletenessReport>(`/api/studies/${id}/completeness-check`),
  generatePlainDescription: (id: number) =>
    api.post<{ documentationId: number; fileName: string }>(
      `/api/studies/${id}/plain-description/generate`),
  listValidationOverrides: (id: number) =>
    api.get<ValidationOverride[]>(`/api/studies/${id}/validation-overrides`),
  upsertValidationOverride: (id: number, data: {
    code: string;
    action: ValidationOverrideAction;
    aiPrompt?: string | null;
    note?: string | null;
  }) =>
    api.post<ValidationOverride>(`/api/studies/${id}/validation-overrides`, data),
  deleteValidationOverride: (id: number, code: string) =>
    api.del<{ ok: boolean; code: string }>(`/api/studies/${id}/validation-overrides/${encodeURIComponent(code)}`),
};

// === Prompty AI per (typ opracowania, sekcja) — admin =====================
export interface AiSectionPromptRow {
  id: number | null;
  section: string;
  /** false dla wpisów w DB nieobecnych w aktualnej liście SEKCJE_PER_TYP — admin może chcieć usunąć. */
  knownInTemplate: boolean;
  promptText: string;
  active: boolean;
  updatedAt: string | null;
}

export interface AiSectionPromptListResponse {
  studyType: string;
  studyTypeName: string;
  rows: AiSectionPromptRow[];
}

export interface AiTypeWithSections {
  studyType: string;
  studyTypeName: string;
  studyTypeAbbr: string;
  studyTypeCode: string;
  sections: string[];
}

export const aiSectionPrompts = {
  types: () => api.get<AiTypeWithSections[]>('/api/admin/ai-section-prompts/types'),
  list: (studyType: string) =>
    api.get<AiSectionPromptListResponse>(
      `/api/admin/ai-section-prompts/${encodeURIComponent(studyType)}`),
  bulkUpsert: (studyType: string, rows: { section: string; promptText: string; active: boolean }[]) =>
    api.put<{ upserted: number; deleted: number }>(
      `/api/admin/ai-section-prompts/${encodeURIComponent(studyType)}`, rows),
  delete: (id: number) =>
    api.del<{ ok: boolean }>(`/api/admin/ai-section-prompts/${id}`),
};

// === Struktura sekcji per typ opracowania (admin) =========================
export interface StudyTypeSectionDto {
  id: number;
  studyType: string;
  section: string;
  sortOrder: number;
  required: boolean;
  legalBasis: string | null;
  description: string | null;
  active: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface StudyTypeSectionTypeOption {
  studyType: string;
  name: string;
  code: string;
  abbreviation: string;
  activeCount: number;
}

export interface StudyTypeSectionInput {
  section?: string | null;
  sortOrder?: number | null;
  required?: boolean | null;
  legalBasis?: string | null;
  description?: string | null;
  active?: boolean | null;
}

export const studyTypeSections = {
  types: () => api.get<StudyTypeSectionTypeOption[]>('/api/admin/study-type-sections/types'),
  list: (studyType: string) =>
    api.get<StudyTypeSectionDto[]>(
      `/api/admin/study-type-sections?type=${encodeURIComponent(studyType)}`),
  create: (studyType: string, body: StudyTypeSectionInput) =>
    api.post<StudyTypeSectionDto>(
      `/api/admin/study-type-sections?type=${encodeURIComponent(studyType)}`, body),
  update: (id: number, body: StudyTypeSectionInput) =>
    api.put<StudyTypeSectionDto>(`/api/admin/study-type-sections/${id}`, body),
  remove: (id: number) =>
    api.del<{ ok: boolean; id: number; active: boolean }>(`/api/admin/study-type-sections/${id}`),
  reorder: (studyType: string, ids: number[]) =>
    api.post<{ ok: boolean; updated: number; total: number }>(
      `/api/admin/study-type-sections/reorder?type=${encodeURIComponent(studyType)}`, { ids }),
};

// === Review po wysyłce do urzędu (pętla rewizji) ===========================
// StudyRejectionDto już zdefiniowany wyżej (interfejs `studyRejections`).
export interface StudyVersionDto {
  documentationId: number;
  version: string;
  createdAt: string;
  fileName: string | null;
  sizeBytes: number | null;
  signed: boolean;
}

export interface SectionDiff {
  section: string;
  oldText: string;
  newText: string;
  changed: boolean;
}

export const studyReview = {
  approve: (studyId: number) =>
    api.post<{ studyId: number; status: string; statusName: string }>(
      `/api/studies/${studyId}/review/approve`),
  requestChanges: (studyId: number, reason: string) =>
    api.post<StudyRejectionDto>(
      `/api/studies/${studyId}/review/request-changes`, { reason }),
  rejections: (studyId: number) =>
    api.get<StudyRejectionDto[]>(`/api/studies/${studyId}/rejections`),
  versions: (studyId: number) =>
    api.get<StudyVersionDto[]>(`/api/studies/${studyId}/versions`),
  diff: (studyId: number, fromDocId: number, toDocId: number) =>
    api.get<SectionDiff[]>(
      `/api/studies/${studyId}/diff?from=${fromDocId}&to=${toDocId}`),
};

export interface AiVerificationIssue {
  severity: 'high' | 'medium' | 'low' | string;
  section: string;
  description: string;
  originalFragment: string;
  suggestedFix: string;
  /** "pending" | "accepted" | "accepted-no-apply" | "rejected" */
  status: string;
}

export interface AiVerificationReport {
  summary: string;
  passed: boolean;
  issues: AiVerificationIssue[];
  aiTracesFound: string;
  provider: string;
  model: string;
  tookMs: number;
  runAt: string | null;
}

export interface ContractStudyRef {
  id: number;
  number: string;
  title: string;
  typeCode: string;
  typeAbbreviation: string;
  deadline: string | null;
}

export interface Contract {
  id: number;
  number: string;
  /** Zlecenia objęte umową — od jednego (umowa prosta) do kilku (umowa kompleksowa). */
  studies: ContractStudyRef[];
  sourceOfferId: number | null;
  sourceOfferNumber: string | null;
  variant: string;
  wariantName: string;
  signedDate: string | null;
  amount: number | null;
  contentMarkdown: string | null;
  templateVersion: string | null;
  signed: boolean;
  acceptedAt: string | null;
  acceptedByName: string | null;
  aiGenerated: boolean;
  aiProvider: string | null;
  aiModel: string | null;
  draft: boolean;
}

export interface ContractAiGenerateRequest {
  studyId: number;
  offerId?: number;
  variant?: string;
  amount?: number;
  instructions?: string;
}

export const contracts = {
  list: (params?: { clientId?: number; studyId?: number }) => {
    const p = new URLSearchParams();
    if (params?.clientId) p.set('clientId', String(params.clientId));
    if (params?.studyId) p.set('studyId', String(params.studyId));
    const qs = p.toString();
    return api.get<Contract[]>('/api/contracts' + (qs ? '?' + qs : ''));
  },
  get: (id: number) => api.get<Contract>(`/api/contracts/${id}`),
  aiGenerate: (data: ContractAiGenerateRequest) =>
    api.post<Contract>('/api/contracts/ai-generate', data),
  aiGenerateFromOffer: (offerId: number, instructions?: string) =>
    api.post<Contract>(`/api/contracts/ai-generate-from-offer/${offerId}`, { instructions }),
  updateDraft: (id: number, data: { contentMarkdown?: string; amount?: number }) =>
    api.put<Contract>(`/api/contracts/${id}/draft`, data),
  regenerate: (id: number, instructions?: string) =>
    api.post<Contract>(`/api/contracts/${id}/regenerate`, { instructions }),
  accept: (id: number) =>
    api.post<Contract>(`/api/contracts/${id}/accept`),
};

export interface CompanyInput {
  code?: string;
  fullName: string;
  shortName: string;
  addressLine1?: string;
  addressLine2?: string;
  addressPostalCode?: string;
  addressCity?: string;
  issuePlace: string;
  phone?: string;
  email?: string;
  website?: string;
  nip?: string;
  regon?: string;
  bankAccount?: string;
  closingText?: string;
  isDefault?: boolean;
  active?: boolean;
}

export const companies = {
  list: () => api.get<Company[]>('/api/companies'),
  adminList: () => api.get<Company[]>('/api/admin/companies'),
  adminGet: (id: number) => api.get<Company>(`/api/admin/companies/${id}`),
  adminCreate: (data: CompanyInput) => api.post<Company>('/api/admin/companies', data),
  adminUpdate: (id: number, data: CompanyInput) => api.put<Company>(`/api/admin/companies/${id}`, data),
  uploadLogo: (id: number, file: File, onProgress?: (p: UploadProgress) => void) => {
    const fd = new FormData();
    fd.append('file', file);
    return xhrUpload<Company>(`/api/admin/companies/${id}/logo`, fd, { onProgress });
  },
  uploadSignature: (id: number, file: File, onProgress?: (p: UploadProgress) => void) => {
    const fd = new FormData();
    fd.append('file', file);
    return xhrUpload<Company>(`/api/admin/companies/${id}/signature`, fd, { onProgress });
  },
};

/** Grupa pozycji (V173) — niesie typ opracowania, bo z grupy powstaje jedno zlecenie. */
export interface OfferLineGroupInput {
  label: string;
  studyType?: string | null;
  studyVariant?: string | null;
  studyDeadline?: string | null; // YYYY-MM-DD
  deadlineMode?: OfferDeadlineMode;
  deadlineQuarter?: number | null;
  deadlineYear?: number | null;
  deadlineText?: string | null;
}

export interface OfferLineItemInput {
  description: string;
  /** Etykieta grupy pozycji, np. nazwa studni — pozycje z tą samą etykietą dostają
   *  na piśmie wspólny nagłówek i podsumę. Puste = pozycja poza grupami (V172). */
  groupLabel?: string | null;
  unit: string;
  quantity: number;
  unitPriceNet: number;
  studyType?: string | null;
  studyVariant?: string | null;
  studyDeadline?: string | null; // YYYY-MM-DD
  // Termin pozycji jako data / kwartał / opis (V160)
  deadlineMode?: OfferDeadlineMode;
  deadlineQuarter?: number | null;
  deadlineYear?: number | null;
  deadlineText?: string | null;
}

export interface OfferSectionInput {
  position?: number;
  heading?: string;
  renderType: string; // OfferSectionRenderType
  content?: string;
}

export interface OfferInput {
  clientId: number;
  intakeId?: number;
  /** Pełna lista ujęć oferty (V71). Gdy podana — wygrywa nad intakeId. */
  intakeIds?: number[];
  companyId?: number;
  type: OfferType;
  title: string;
  scope?: string;
  recipientHtml?: string;
  subjectHtml?: string;
  bodyHtml?: string;
  legalBasisHtml?: string;
  termsHtml?: string;
  extraCostsHtml?: string;
  unitPrice?: number;
  unitCount?: number;
  unitLabel?: string;
  /** LUMP_SUM — typ opracowania całej oferty (V29) */
  studyType?: string | null;
  studyVariant?: string | null;
  studyDeadline?: string | null;
  // Termin realizacji jako data / kwartał / opis + czy drukować go na piśmie (V160)
  deadlineMode?: OfferDeadlineMode;
  deadlineQuarter?: number | null;
  deadlineYear?: number | null;
  deadlineText?: string | null;
  printDeadline?: boolean;
  groups?: OfferLineGroupInput[];
  lineItems?: OfferLineItemInput[];
  issueDate?: string;
  validUntil?: string;
  paymentTerms?: string;
  vatRate?: number;
  notes?: string;
  // Rodzaj oferty + powiązany otwór + sekcje treści (V89)
  kindId?: number;
  boreholeId?: number;
  /** Studnie ujęcia objęte ofertą (V187); pierwsza = główna. Zastępuje boreholeId. */
  boreholeIds?: number[];
  sections?: OfferSectionInput[];
  /** Czy drukować kwotę brutto w treści pisma (V160). */
  showGross?: boolean;
  /** Czy wstawić pieczątkę z podpisem (V160). */
  includeSignature?: boolean;
}

export interface OfferTemplateInput {
  code?: string;
  name: string;
  workType: string;
  description?: string;
  sampleSubject?: string;
  sampleBody?: string;
  sampleLegalBasis?: string;
  sampleTerms?: string;
  sampleExtraCosts?: string;
  styleNotes?: string;
  active?: boolean;
}

export const offerTemplates = {
  active: (workType?: string) => {
    const qs = workType ? '?workType=' + encodeURIComponent(workType) : '';
    return api.get<OfferTemplate[]>('/api/offer-templates' + qs);
  },
  workTypes: () => api.get<WorkType[]>('/api/offer-templates/work-types'),
  adminList: () => api.get<OfferTemplate[]>('/api/admin/offer-templates'),
  adminGet: (id: number) => api.get<OfferTemplate>(`/api/admin/offer-templates/${id}`),
  adminCreate: (data: OfferTemplateInput) => api.post<OfferTemplate>('/api/admin/offer-templates', data),
  adminUpdate: (id: number, data: OfferTemplateInput) => api.put<OfferTemplate>(`/api/admin/offer-templates/${id}`, data),
  adminDelete: (id: number) => api.del<void>(`/api/admin/offer-templates/${id}`),
};

export interface ExtractedLayer {
  fromDepth: number | null;
  toDepth: number | null;
  symbol: string | null;
  description: string | null;
}

export interface ExtractedBoreholeData {
  // Podstawowe
  designation: string | null;
  function: string | null;
  depth: number | null;
  diameterMm: number | null;
  yieldM3h: number | null;
  yearCompleted: number | null;
  latitude: number | null;
  longitude: number | null;
  avgDailyIntakeM3: number | null;
  // Obudowa
  housingTypeName: string | null;
  housingDiameterMm: number | null;
  housingDepth: number | null;
  housingCover: string | null;
  housingSteelLadderDiameterMm: number | null;
  housingHydraulicFittings: string | null;
  // Filtr
  filterFromDepth: number | null;
  filterToDepth: number | null;
  filterType: string | null;
  filterDiameterMm: number | null;
  // Pompa
  pumpType: string | null;
  pumpModel: string | null;
  pumpInstallDepth: number | null;
  pumpDeliveryPipeDiameterMm: number | null;
  pumpMaxCapacityM3h: number | null;
  // Wodomierz
  meterType: string | null;
  meterModel: string | null;
  meterAccuracy: string | null;
  meterLocation: string | null;
  // Hydraulika
  pumpingTestQM3h: number | null;
  pumpingTestDrawdownM: number | null;
  pumpingTestSpecificCapacity: number | null;
  pumpingTestDurationH: number | null;
  permissibleDrawdownM: number | null;
  permeabilityKMs: number | null;
  transmissivityTM2s: number | null;
  // Profil
  layers: ExtractedLayer[];
}

export interface ExtractedIntakeData {
  name: string | null;
  town: string | null;
  county: string | null;
  voivodeship: string | null;
  latitude: number | null;
  longitude: number | null;
  qM3h: number | null;
  jcwpdCode: string | null;
  jcwpdName: string | null;
  regionWodny: string | null;
  dorzecze: string | null;
  approvedResourceM3h: number | null;
  approvedResourceDecisionRef: string | null;
  boreholes: ExtractedBoreholeData[];
  /** Pola które AI faktycznie znalazł w dokumencie — UI je podświetla. */
  extractedFields: string[];
  sourceTextPreview: string | null;
  provider: string | null;
  model: string | null;
}

export interface OfferAiLineItem {
  studyTypeCode?: string;
  studyTypeFriendlyName?: string;
  variant?: string;
  description?: string;
}

export interface OfferAiGenerateRequest {
  clientId?: number;
  intakeId?: number;
  workType: string;
  briefDescription?: string;
  location?: string;
  unitPriceNet?: number;
  unitCount?: number;
  unitLabel?: string;
  /** Konkretna lista opracowań z friendly names (bez kodów PRG_1A) — zapobiega temu
   *  że AI opisze inne opracowanie niż wybrane. */
  lineItems?: OfferAiLineItem[];
}

export interface OfferEmailDefaults {
  to: string;
  subject: string;
  body: string;
  attachmentName: string;
}

export interface OfferEmailRequest {
  to: string;
  cc?: string[];
  bcc?: string[];
  subject: string;
  bodyHtml: string;
  attachPdf: boolean;
  attachDocx: boolean;
}

export const offers = {
  list: (status?: OfferStatus, clientId?: number) => {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (clientId) p.set('clientId', String(clientId));
    const qs = p.toString();
    return api.get<Offer[]>('/api/offers' + (qs ? '?' + qs : ''));
  },
  get: (id: number) => api.get<Offer>(`/api/offers/${id}`),
  stats: () => api.get<Record<string, number>>('/api/offers/stats'),
  /** Teksty „Cena za…” użyte w dotychczasowych ofertach, najczęstsze pierwsze. */
  unitLabels: () => api.get<string[]>('/api/offers/unit-labels'),
  create: (data: OfferInput) => api.post<Offer>('/api/offers', data),
  /** Podgląd pisma dla oferty jeszcze niezapisanej — nic nie zapisuje (V160). */
  preview: (data: OfferInput) => api.post<OfferDocument>('/api/offers/preview', data),
  update: (id: number, data: OfferInput) => api.put<Offer>(`/api/offers/${id}`, data),
  changeStatus: (id: number, status: OfferStatus) =>
    api.post<Offer>(`/api/offers/${id}/status`, { status }),
  emailDefaults: (id: number) => api.get<OfferEmailDefaults>(`/api/offers/${id}/email/defaults`),
  sendEmail: (id: number, data: OfferEmailRequest) =>
    api.post<Offer>(`/api/offers/${id}/email`, data),
  aiGenerate: (data: OfferAiGenerateRequest) =>
    api.post<AiGeneratedOffer>('/api/offers/ai-generate', data),
  /** Ścieżka B akceptacji oferty: klient przysłał własne zlecenie zamiast podpisywać umowę.
   *  Geolog wgrywa plik (PDF/skan) i system tworzy Study bez Contract.
   *  Zwraca { studyId, attachmentId } do redirectu na StudyDetail. */
  startWithoutContract: (
    offerId: number,
    file: File | null,
    data: {
      intakeId: number;
      studyType: string;
      title?: string;
      orderDate?: string | null;
      deadline?: string | null;
      budget?: number | null;
      priority?: string | null;
      managerId?: number | null;
      geologistId?: number | null;
      attachmentDescription?: string | null;
    },
    onProgress?: (p: UploadProgress) => void,
  ): Promise<{ studyId: number; attachmentId: number | null }> => {
    const fd = new FormData();
    if (file) fd.append('file', file);
    fd.append('data', new Blob([JSON.stringify(data)], { type: 'application/json' }));
    return xhrUpload<{ studyId: number; attachmentId: number | null }>(
      `/api/offers/${offerId}/start-without-contract`, fd, { onProgress });
  },
  /** Auto-tekst „Dane dotyczące studni" (Klucz: Wartość) z danych otworu — do sekcji SPEC_TABLE. */
  boreholeSpec: (boreholeId: number) =>
    api.get<{ boreholeId: number; content: string }>(`/api/offers/borehole-spec/${boreholeId}`),
  /** Ścieżka „zlecenie": oznacza ofertę jako zlecenie + opcjonalny dokument klienta. */
  markOrdered: (
    offerId: number,
    file: File | null,
    data?: { orderDate?: string | null; description?: string | null },
    onProgress?: (p: UploadProgress) => void,
  ): Promise<{ attachmentId: number | null }> => {
    const fd = new FormData();
    if (file) fd.append('file', file);
    if (data?.orderDate) fd.append('orderDate', data.orderDate);
    if (data?.description) fd.append('description', data.description);
    return xhrUpload<{ attachmentId: number | null }>(
      `/api/offers/${offerId}/mark-ordered`, fd, { onProgress });
  },
  attachments: (offerId: number) =>
    api.get<OfferAttachmentDto[]>(`/api/offers/${offerId}/attachments`),
  attachmentCategories: () =>
    api.get<{ value: OfferAttachmentCategory; label: string }[]>('/api/offer-attachment-categories'),
  /** Plik trafia do archiwum ujęcia oferty (lub `intakeId`), a z `boreholeId` także do otworu. */
  uploadAttachment: (offerId: number, file: File, opts?: {
    category?: OfferAttachmentCategory;
    description?: string;
    receivedAt?: string;
    intakeId?: number;
    boreholeId?: number;
  }, onProgress?: (p: UploadProgress) => void) => {
    const fd = new FormData();
    fd.append('file', file);
    if (opts?.category) fd.append('category', opts.category);
    if (opts?.description) fd.append('description', opts.description);
    if (opts?.receivedAt) fd.append('receivedAt', opts.receivedAt);
    if (opts?.intakeId != null) fd.append('intakeId', String(opts.intakeId));
    if (opts?.boreholeId != null) fd.append('boreholeId', String(opts.boreholeId));
    return xhrUpload<OfferAttachmentDto>(`/api/offers/${offerId}/attachments`, fd, { onProgress });
  },
  deleteAttachment: (offerId: number, attachmentId: number) =>
    api.del<{ ok: boolean }>(`/api/offers/${offerId}/attachments/${attachmentId}`),
};

// ---- Załączniki oferty + konfigurowalne rodzaje ofert (V89) ----

export type OfferAttachmentCategory =
  | 'INQUIRY' | 'BOREHOLE_CARD' | 'CLIENT_ORDER' | 'PROTOCOL' | 'REPORT' | 'CORRESPONDENCE' | 'OTHER';

export interface OfferAttachmentDto {
  id: number;
  archiveDocumentId?: number;
  fileName?: string;
  sizeBytes?: number;
  category: OfferAttachmentCategory;
  categoryName: string;
  description?: string;
  receivedAt?: string;
  createdAt?: string;
  downloadUrl?: string;
  offerId?: number;
  offerNumber?: string;
  /** Ujęcie i otwór, przy których plik leży w archiwum. */
  intakeId?: number | null;
  intakeName?: string | null;
  boreholeId?: number | null;
  boreholeDesignation?: string | null;
  createdByName?: string | null;
}

export interface OfferKindSectionItemTpl {
  id?: number;
  sortOrder?: number;
  text: string;
  defaultChecked?: boolean;
  active?: boolean;
}

export interface OfferKindSectionTpl {
  id?: number;
  sortOrder?: number;
  heading?: string;
  renderType: string;        // OfferSectionRenderType
  renderTypeName?: string;
  defaultContent?: string;
  active?: boolean;
  /** Biblioteka pozycji (checkbox) — tylko sekcje listowe. Pusta = zwykłe pole tekstowe. */
  items?: OfferKindSectionItemTpl[];
}

export interface OfferKind {
  id: number;
  code: string;
  name: string;
  abbreviation?: string;
  purpose: string;
  purposeName?: string;
  followUp: string;
  followUpName?: string;
  defaultPricingMode: string;
  defaultPricingModeName?: string;
  numberFormat?: string;
  usesBoreholeData: boolean;
  secondSignatureLabel?: string;
  /** Szablon przedmiotu („Na wykonanie…") auto-uzupełniany w kreatorze; placeholdery {ujecie}/{miejscowosc}/{studnia}. */
  subjectTemplate?: string;
  description?: string;
  sortOrder: number;
  active: boolean;
  sections: OfferKindSectionTpl[];
}

export interface OfferKindInput {
  code: string;
  name: string;
  abbreviation?: string;
  purpose?: string;
  followUp?: string;
  defaultPricingMode?: string;
  numberFormat?: string;
  usesBoreholeData?: boolean;
  secondSignatureLabel?: string;
  subjectTemplate?: string;
  description?: string;
  sortOrder?: number;
  active?: boolean;
  sections?: OfferKindSectionTpl[];
}

export interface OfferKindMeta {
  purposes: { value: string; label: string }[];
  followUps: { value: string; label: string }[];
  pricingModes: { value: string; label: string }[];
  renderTypes: { value: string; label: string }[];
}

export const offerKinds = {
  list: (includeInactive?: boolean) =>
    api.get<OfferKind[]>('/api/offer-kinds' + (includeInactive ? '?includeInactive=true' : '')),
  meta: () => api.get<OfferKindMeta>('/api/offer-kinds/meta'),
  create: (data: OfferKindInput) => api.post<OfferKind>('/api/offer-kinds', data),
  update: (id: number, data: Partial<OfferKindInput>) => api.put<OfferKind>(`/api/offer-kinds/${id}`, data),
  delete: (id: number) => api.del<{ ok: boolean; deactivated: boolean }>(`/api/offer-kinds/${id}`),
};

export interface ComplianceDeadlineInput {
  category: ComplianceCategory;
  title: string;
  description?: string | null;
  companyId?: number | null;
  userId?: number | null;
  responsibleUserId?: number | null;
  dueDate: string;
  recurrenceMonths?: number | null;
  remindDaysBefore?: number | null;
}

export interface ComplianceMeta {
  categories: { id: ComplianceCategory; name: string }[];
  statuses: { id: ComplianceStatus; name: string; color: string }[];
}

export const complianceDeadlines = {
  list: (params?: { status?: ComplianceStatus; category?: ComplianceCategory }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.category) qs.set('category', params.category);
    const s = qs.toString();
    return api.get<ComplianceDeadline[]>('/api/compliance-deadlines' + (s ? '?' + s : ''));
  },
  get: (id: number) => api.get<ComplianceDeadline>(`/api/compliance-deadlines/${id}`),
  meta: () => api.get<ComplianceMeta>('/api/compliance-deadlines/meta'),
  create: (data: ComplianceDeadlineInput) =>
    api.post<ComplianceDeadline>('/api/compliance-deadlines', data),
  update: (id: number, data: ComplianceDeadlineInput) =>
    api.put<ComplianceDeadline>(`/api/compliance-deadlines/${id}`, data),
  complete: (id: number, completedDate?: string | null) =>
    api.post<ComplianceDeadline>(`/api/compliance-deadlines/${id}/complete`, { completedDate: completedDate ?? null }),
  cancel: (id: number) => api.post<void>(`/api/compliance-deadlines/${id}/cancel`, {}),
  remove: (id: number) => api.del<void>(`/api/compliance-deadlines/${id}`),
};

// === Kadry / HR (V77) ==============================================================
export const employees = {
  list: (status?: EmploymentStatus) =>
    api.get<Employee[]>('/api/employees' + (status ? '?status=' + status : '')),
  get: (id: number) => api.get<Employee>(`/api/employees/${id}`),
  meta: () => api.get<EmployeeMeta>('/api/employees/meta'),
  create: (data: EmployeeInput) => api.post<Employee>('/api/employees', data),
  update: (id: number, data: EmployeeInput) => api.put<Employee>(`/api/employees/${id}`, data),
  remove: (id: number) => api.del<void>(`/api/employees/${id}`),
  contracts: {
    add: (employeeId: number, data: ContractInput) =>
      api.post<EmploymentContract>(`/api/employees/${employeeId}/contracts`, data),
    update: (employeeId: number, contractId: number, data: ContractInput) =>
      api.put<EmploymentContract>(`/api/employees/${employeeId}/contracts/${contractId}`, data),
    remove: (employeeId: number, contractId: number) =>
      api.del<void>(`/api/employees/${employeeId}/contracts/${contractId}`),
  },
  qualifications: {
    add: (employeeId: number, data: QualificationInput) =>
      api.post<EmployeeQualification>(`/api/employees/${employeeId}/qualifications`, data),
    update: (employeeId: number, qualId: number, data: QualificationInput) =>
      api.put<EmployeeQualification>(`/api/employees/${employeeId}/qualifications/${qualId}`, data),
    remove: (employeeId: number, qualId: number) =>
      api.del<void>(`/api/employees/${employeeId}/qualifications/${qualId}`),
  },
  // ── Ewidencja czasu pracy / nadgodziny (V79) ──
  timesheet: (employeeId: number, year: number, month: number) =>
    api.get<Timesheet>(`/api/employees/${employeeId}/timesheet?year=${year}&month=${month}`),
  workTimeEntries: {
    add: (employeeId: number, data: WorkTimeEntryInput) =>
      api.post<WorkTimeEntry>(`/api/employees/${employeeId}/work-time-entries`, data),
    update: (employeeId: number, entryId: number, data: WorkTimeEntryInput) =>
      api.put<WorkTimeEntry>(`/api/employees/${employeeId}/work-time-entries/${entryId}`, data),
    remove: (employeeId: number, entryId: number) =>
      api.del<void>(`/api/employees/${employeeId}/work-time-entries/${entryId}`),
  },
  settlement: (employeeId: number, year: number, month: number, data: SettlementInput, finalize = false) =>
    api.put<MonthlySettlement>(
      `/api/employees/${employeeId}/settlements/${year}/${month}` + (finalize ? '?finalize=true' : ''), data),
  payroll: {
    get: (employeeId: number) => api.get<EmployeePayroll>(`/api/employees/${employeeId}/payroll`),
    update: (employeeId: number, data: EmployeePayrollInput) =>
      api.put<EmployeePayroll>(`/api/employees/${employeeId}/payroll`, data),
  },
  prefillFromCalendar: (employeeId: number, from: string, to: string) =>
    api.post<{ created: number }>(`/api/employees/${employeeId}/timesheet/prefill-from-calendar?from=${from}&to=${to}`),
  timesheetXlsxUrl: (employeeId: number, year: number, month: number) =>
    `/api/employees/${employeeId}/timesheet/xlsx?year=${year}&month=${month}`,
  timesheetPdfUrl: (employeeId: number, year: number, month: number) =>
    `/api/employees/${employeeId}/timesheet/pdf?year=${year}&month=${month}`,
  dwutygodniowkaXlsxUrl: (employeeId: number, startMonday: string, prefill: boolean) =>
    `/api/employees/${employeeId}/dwutygodniowka/xlsx?startMonday=${startMonday}${prefill ? '&prefill=true' : ''}`,
  dwutygodniowkaPdfUrl: (employeeId: number, startMonday: string, prefill: boolean) =>
    `/api/employees/${employeeId}/dwutygodniowka/pdf?startMonday=${startMonday}${prefill ? '&prefill=true' : ''}`,
  // ── Dwutygodniówka ze zdjęcia (AI OCR) ──
  dwutygodniowkaExtract: (employeeId: number, file: File, startMonday: string, onProgress?: (p: UploadProgress) => void): Promise<DwutygodniowkaExtraction> => {
    const fd = new FormData();
    fd.append('file', file);
    return xhrUpload<DwutygodniowkaExtraction>(
      `/api/employees/${employeeId}/dwutygodniowka/extract?startMonday=${startMonday}`, fd, { onProgress });
  },
  dwutygodniowkaBulkExtract: (files: File[], onProgress?: (p: UploadProgress) => void): Promise<BulkExtractResult[]> => {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    return xhrUpload<BulkExtractResult[]>('/api/employees/dwutygodniowka/bulk-extract', fd, { onProgress });
  },
  /** Odczyt pojedynczego zdjęcia — używane przez odczyt masowy z paskiem postępu (plik po pliku). */
  dwutygodniowkaAutoExtract: (file: File, signal?: AbortSignal, onProgress?: (p: UploadProgress) => void): Promise<BulkExtractResult> => {
    const fd = new FormData();
    fd.append('file', file);
    return xhrUpload<BulkExtractResult>('/api/employees/dwutygodniowka/auto-extract', fd, { onProgress, signal });
  },
  dwutygodniowkaConfirm: (employeeId: number, startMonday: string, rows: DwuConfirmRow[], file?: File | null, onProgress?: (p: UploadProgress) => void): Promise<{ created: number; archiveDocumentId: number }> => {
    const fd = new FormData();
    fd.append('payload', new Blob([JSON.stringify({ startMonday, rows })], { type: 'application/json' }));
    if (file) fd.append('file', file);
    return xhrUpload<{ created: number; archiveDocumentId: number }>(
      `/api/employees/${employeeId}/dwutygodniowka/confirm`, fd, { onProgress });
  },
  // ── Urlopy (V83) ──
  leave: {
    overview: (employeeId: number, year: number) =>
      api.get<LeaveOverview>(`/api/employees/${employeeId}/leave?year=${year}`),
    add: (employeeId: number, data: LeaveInput) =>
      api.post<LeaveRequest>(`/api/employees/${employeeId}/leave`, data),
    update: (employeeId: number, leaveId: number, data: LeaveInput) =>
      api.put<LeaveRequest>(`/api/employees/${employeeId}/leave/${leaveId}`, data),
    remove: (employeeId: number, leaveId: number) =>
      api.del<void>(`/api/employees/${employeeId}/leave/${leaveId}`),
    updateEntitlement: (employeeId: number, year: number, data: EntitlementInput) =>
      api.put<LeaveBalance>(`/api/employees/${employeeId}/leave/entitlement?year=${year}`, data),
    // Wniosek urlopowy do wydruku i podpisu (V140).
    wniosekPdfUrl: (employeeId: number, leaveId: number) =>
      `/api/employees/${employeeId}/leave/${leaveId}/wniosek.pdf`,
    setSigned: (employeeId: number, leaveId: number, signed: boolean, signedAt?: string | null) =>
      api.put<LeaveRequest>(`/api/employees/${employeeId}/leave/${leaveId}/signed`, { signed, signedAt: signedAt ?? null }),
  },
};

// === Urlopy — widok firmowy (V83) ==================================================
export const leaves = {
  range: (from: string, to: string) =>
    api.get<LeaveRequest[]>(`/api/leaves?from=${from}&to=${to}`),
  meta: () => api.get<LeaveMeta>('/api/leaves/meta'),
};

// === Finanse / Faktury (V84) =======================================================
export const invoices = {
  /** @param clientId zawęża do faktur tego kontrahenta (dopasowanie po counterpartyClient). */
  list: (direction: InvoiceDirection, status?: string, clientId?: number) =>
    api.get<Invoice[]>(`/api/invoices?direction=${direction}`
      + (status ? `&status=${status}` : '')
      + (clientId ? `&clientId=${clientId}` : '')),
  get: (id: number) => api.get<Invoice>(`/api/invoices/${id}`),
  /** Podgląd faktury: nagłówek + powiązane przelewy + historia dokumentu (jeden request). */
  detail: (id: number) => api.get<InvoiceDetail>(`/api/invoices/${id}/detail`),
  sync: (days = 7) => api.post<{ purchase: number; sales: number }>(`/api/invoices/sync?days=${days}`),
  setPaymentStatus: (id: number, data: PaymentStatusInput) =>
    api.put<Invoice>(`/api/invoices/${id}/payment-status`, data),
  togglePaid: (id: number) => api.post<Invoice>(`/api/invoices/${id}/toggle-paid`),
  meta: () => api.get<InvoiceMeta>('/api/invoices/meta'),
  /** Faktura wprowadzona ręcznie — zagraniczni dostawcy i dokumenty papierowe (poza KSeF). */
  createManual: (data: ManualInvoiceInput) => api.post<Invoice>('/api/invoices', data),
};

// === Subskrypcje i płatności cykliczne (V153) ===

export const vendors = {
  list: (onlyActive = false) =>
    api.get<Vendor[]>(`/api/vendors${onlyActive ? '?onlyActive=true' : ''}`),
  get: (id: number) => api.get<Vendor>(`/api/vendors/${id}`),
  create: (data: VendorInput) => api.post<Vendor>('/api/vendors', data),
  update: (id: number, data: VendorInput) => api.put<Vendor>(`/api/vendors/${id}`, data),
  remove: (id: number) => api.del<void>(`/api/vendors/${id}`),
};

export const subscriptions = {
  list: (params?: { status?: SubscriptionStatusCode; category?: SubscriptionCategoryCode }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.category) q.set('category', params.category);
    const qs = q.toString();
    return api.get<Subscription[]>(`/api/subscriptions${qs ? `?${qs}` : ''}`);
  },
  /** Nagłówek + wszystkie okresy jednym requestem. */
  detail: (id: number) => api.get<SubscriptionDetail>(`/api/subscriptions/${id}`),
  create: (data: SubscriptionInput) => api.post<SubscriptionDetail>('/api/subscriptions', data),
  update: (id: number, data: SubscriptionInput) => api.put<SubscriptionDetail>(`/api/subscriptions/${id}`, data),
  remove: (id: number) => api.del<void>(`/api/subscriptions/${id}`),
  upcoming: (days = 60) => api.get<SubscriptionPeriod[]>(`/api/subscriptions/upcoming?days=${days}`),
  meta: () => api.get<SubscriptionMeta>('/api/subscriptions/meta'),

  // ── Okresy ──
  updatePeriod: (periodId: number, data: { dueDate?: string | null; plannedAmount?: number | null; notes?: string | null }) =>
    api.put<SubscriptionPeriod>(`/api/subscriptions/periods/${periodId}`, data),
  markPaid: (periodId: number, data?: { paidDate?: string | null; paidAmount?: number | null }) =>
    api.post<SubscriptionPeriod>(`/api/subscriptions/periods/${periodId}/mark-paid`, data ?? {}),
  unmarkPaid: (periodId: number) =>
    api.post<SubscriptionPeriod>(`/api/subscriptions/periods/${periodId}/unmark-paid`),
  skip: (periodId: number, skip: boolean) =>
    api.post<SubscriptionPeriod>(`/api/subscriptions/periods/${periodId}/skip?skip=${skip}`),
  /** Podpowiedzi faktur z KSeF pasujących do okresu (sortowane od najpewniejszej). */
  invoiceCandidates: (periodId: number) =>
    api.get<InvoiceCandidate[]>(`/api/subscriptions/periods/${periodId}/invoice-candidates`),
  attachInvoice: (periodId: number, invoiceId: number) =>
    api.post<SubscriptionPeriod>(`/api/subscriptions/periods/${periodId}/invoice`, { invoiceId }),
  detachInvoice: (periodId: number) =>
    api.del<SubscriptionPeriod>(`/api/subscriptions/periods/${periodId}/invoice`),
};

export const bank = {
  transactions: (status?: BankTxMatchStatus) =>
    api.get<BankTransaction[]>('/api/bank/transactions' + (status ? `?status=${status}` : '')),
  confirm: (id: number, invoiceId: number) =>
    api.post<BankTransaction>(`/api/bank/transactions/${id}/confirm?invoiceId=${invoiceId}`),
  reject: (id: number) => api.post<BankTransaction>(`/api/bank/transactions/${id}/reject`),
  sync: (days = 14) =>
    api.post<{ imported: number; matched: number; skipped: number }>(`/api/bank/sync?days=${days}`),
  meta: () => api.get<BankMeta>('/api/bank/meta'),
  importStatement: (file: File, onProgress?: (p: UploadProgress) => void): Promise<{ imported: number; matched: number; skipped: number }> => {
    const fd = new FormData();
    fd.append('file', file);
    return xhrUpload<{ imported: number; matched: number; skipped: number }>('/api/bank/import', fd, { onProgress });
  },
};

// === Pojazdy / Sprzęt (V77) ========================================================
export const vehicles = {
  list: (status?: VehicleStatus) =>
    api.get<Vehicle[]>('/api/vehicles' + (status ? '?status=' + status : '')),
  get: (id: number) => api.get<Vehicle>(`/api/vehicles/${id}`),
  meta: () => api.get<VehicleMeta>('/api/vehicles/meta'),
  create: (data: VehicleInput) => api.post<Vehicle>('/api/vehicles', data),
  update: (id: number, data: VehicleInput) => api.put<Vehicle>(`/api/vehicles/${id}`, data),
  remove: (id: number) => api.del<void>(`/api/vehicles/${id}`),
  services: {
    add: (vehicleId: number, data: VehicleServiceInput) =>
      api.post<VehicleService>(`/api/vehicles/${vehicleId}/services`, data),
    update: (vehicleId: number, serviceId: number, data: VehicleServiceInput) =>
      api.put<VehicleService>(`/api/vehicles/${vehicleId}/services/${serviceId}`, data),
    remove: (vehicleId: number, serviceId: number) =>
      api.del<void>(`/api/vehicles/${vehicleId}/services/${serviceId}`),
  },
  readings: {
    list: (vehicleId: number) =>
      api.get<VehicleOdometerReading[]>(`/api/vehicles/${vehicleId}/readings`),
    add: (vehicleId: number, data: VehicleOdometerReadingInput) =>
      api.post<VehicleOdometerReading>(`/api/vehicles/${vehicleId}/readings`, data),
    remove: (vehicleId: number, readingId: number) =>
      api.del<void>(`/api/vehicles/${vehicleId}/readings/${readingId}`),
  },
};

// === Kalendarz brygad (V77) ========================================================
export const trips = {
  range: (from: string, to: string) =>
    api.get<Trip[]>(`/api/trips?from=${from}&to=${to}`),
  get: (id: number) => api.get<Trip>(`/api/trips/${id}`),
  meta: () => api.get<TripMeta>('/api/trips/meta'),
  create: (data: TripInput) => api.post<Trip>('/api/trips', data),
  update: (id: number, data: TripInput) => api.put<Trip>(`/api/trips/${id}`, data),
  remove: (id: number) => api.del<void>(`/api/trips/${id}`),
};

export const brigades = {
  list: (onlyActive = false) =>
    api.get<Brigade[]>('/api/brigades' + (onlyActive ? '?onlyActive=true' : '')),
  create: (data: BrigadeInput) => api.post<Brigade>('/api/brigades', data),
  update: (id: number, data: BrigadeInput) => api.put<Brigade>(`/api/brigades/${id}`, data),
  remove: (id: number) => api.del<void>(`/api/brigades/${id}`),
};

export const tasks = {
  kanban: () => api.get<Record<string, Task[]>>('/api/tasks/kanban'),
  mine: () => api.get<Task[]>('/api/tasks/mine'),
  /** Zadania z tablicy, które zalogowany może przyjąć (typ opracowania z jego profilu albo bez typu). */
  available: () => api.get<Task[]>('/api/tasks/available'),
  accept: (id: number) => api.post<Task>(`/api/tasks/${id}/accept`),
  move: (id: number, status: string, position: number) =>
    api.post<{ id: number; status: string; position: number }>(
      `/api/tasks/${id}/move`, { status, position }),
  create: (data: TaskInput) => api.post<Task>('/api/tasks', data),
  update: (id: number, data: TaskInput) => api.put<Task>(`/api/tasks/${id}`, data),
  remove: (id: number) => api.del<{ ok: boolean }>(`/api/tasks/${id}`),
  assign: (id: number, userId: number | null) =>
    api.post<Task>(`/api/tasks/${id}/assign`, { userId }),
  setPriority: (id: number, priority: string) =>
    api.post<Task>(`/api/tasks/${id}/priority`, { priority }),
  setDeadline: (id: number, deadline: string | null) =>
    api.post<Task>(`/api/tasks/${id}/deadline`, { deadline }),
};

export const workflow = {
  rozpocznij: (krokId: number) => api.post<{ id: number; status: string }>(`/api/workflow/step/${krokId}/start`),
  zakoncz: (krokId: number) => api.post<{ id: number; status: string; opracowanieStatus: string }>(`/api/workflow/step/${krokId}/finish`),
  pomin: (krokId: number, note?: string) => api.post<{ ok: boolean }>(`/api/workflow/step/${krokId}/skip`, { note }),
  assign: (krokId: number, userId: number | null) =>
    api.post<{ id: number; assigneeId: number | null; assigneeName: string | null }>(
      `/api/workflow/step/${krokId}/assign`,
      { userId }
    ),
};

export interface AssignableUser {
  id: number;
  fullName: string;
  initials: string;
}

export const users = {
  /**
   * Lista osób do przypisania. `permission` zawęża ją po stronie serwera do osób mających
   * dane uprawnienie (np. STUDY_SIGN dla pola „geolog uprawniony") — front nie zna już kodów
   * ról, więc rola utworzona w macierzy trafia do pickerów na równi z wbudowaną.
   */
  assignable: (permission?: string) =>
    api.get<AssignableUser[]>('/api/users/assignable' + (permission ? `?permission=${permission}` : '')),
};

export const wizard = {
  proposal: (q: number) => api.get<AuthorityProposal>(`/api/intake-wizard/proposal?q=${q}`),
  recommendation: (
    typeCode: string, authority: string, odpowiedzi: Record<string, boolean>
  ) => api.post<VariantRecommendation>('/api/contract-wizard/recommendation', { typeCode, authority, odpowiedzi }),
};

export const ai = {
  generate: (studyId: number, section: string) => {
    const params = new URLSearchParams();
    params.set('studyId', String(studyId));
    params.set('section', section);
    return fetch('/api/ai/generate?' + params, { method: 'POST', credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error('AI: ' + r.status); return r.json() as Promise<AiDraft>; });
  },
  accept: (draftId: number, editedText?: string) =>
    api.post<AiDraft>(`/api/ai/accept/${draftId}`, { editedText }),
  unaccept: (draftId: number) =>
    api.post<AiDraft>(`/api/ai/unaccept/${draftId}`),
  regenerate: (draftId: number, instrukcje?: string) =>
    api.post<{ id: number; text: string; czasMs: number }>(`/api/ai/regenerate/${draftId}`, { instrukcje }),
  /** Bulk-generacja wszystkich sekcji draftu opracowania na podstawie kompletu dokumentów + RAG. */
  generateAllDrafts: (studyId: number) =>
    api.post<{
      requested: number;
      draftIds: number[];
      generatedSections: string[];
      failedSections: string[];
      firstError: string | null;
    }>(`/api/ai/studies/${studyId}/generate-all-drafts`),
  /** Lista sekcji dla podanego typu opracowania (różna lista dla analizy ryzyka, dok. hydrogeol., etc.). */
  sectionsForType: (studyType: string) =>
    api.get<string[]>(`/api/ai/sections/${encodeURIComponent(studyType)}`),
};

export interface MapFetchRequest {
  studyId: number;
  layerCodes: string[];
  format: 'A2' | 'A3' | 'A4';
  scale: string;
  fileType: 'PNG' | 'PDF';
  bbox?: [number, number, number, number]; // EPSG:4326 [minLng, minLat, maxLng, maxLat]
  name?: string;          // Opis mapy w metryce (np. „Mapa topograficzna")
  drawnBy?: string;       // Kreślił (metryka)
  drawingNumber?: string; // Rys. nr (metryka)
}

export interface MapPhaseUpdate {
  key: string;
  label: string;
  current?: number;
  total?: number;
  elapsedMs?: number;
}

export interface MapFetchResult {
  fileKey: string;
  filename: string;
  sizeBytes: number;
  storageUrl: string;
}

export interface MapLayerInput {
  code: string;
  name: string;
  source: string;
  protocol: 'WMS' | 'WMTS' | 'XYZ';
  url: string;
  layerId?: string;
  imageFormat?: string;
  /** WMTS: TILEMATRIXSET (np. EPSG:3857). */
  tileMatrixSet?: string;
  /** WMTS: STYLE (zwykle „default"). */
  wmtsStyle?: string;
  category: string;
  defaultOpacity: number;
  enabledByDefault: boolean;
}

export interface EgibParcelProperties {
  id: string;
  number: string | null;
  obrebNumer: string | null;
  obrebNazwa: string | null;
  gminaNazwa: string | null;
  poleEwidencyjne: string | null;
  klasouzytki: string | null;
  dataEgib: string | null;
}
export interface EgibParcelFeatureCollection {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    id: string;
    properties: EgibParcelProperties;
    geometry: { type: 'Polygon' | 'MultiPolygon'; coordinates: unknown };
  }>;
  meta?: { note?: string; intakeId?: number; bufferM?: number; center?: [number, number]; count?: number };
}

/** Wiersz eksportu działek — jeden do jednego z tabelą „Działki w obszarze badań (EGIB)". */
export interface ParcelExportRow {
  egibId: string;
  parcelNumber: string | null;
  obrebNumer: string | null;
  obrebNazwa: string | null;
  gminaNazwa: string | null;
  poleM2: number | null;
  klasouzytki: string | null;
  dataEgib: string | null;
  /** true = działka jest w operacie (zapisana), false = tylko znaleziona w buforze. */
  saved: boolean;
}

export const egib = {
  parcelsInBbox: (bbox: [number, number, number, number]) =>
    api.get<EgibParcelFeatureCollection>(`/api/egib/parcels?bbox=${bbox.join(',')}`),
  parcelsForIntake: (intakeId: number, bufferM: number) =>
    api.get<EgibParcelFeatureCollection>(`/api/intakes/${intakeId}/parcels?bufferM=${bufferM}`),
  /**
   * Eksportuje podane wiersze do CSV/XLSX i od razu zapisuje plik u użytkownika.
   * Wiersze wysyłamy z frontu, żeby plik był dokładnie tym, co widać w tabeli (bez powtórnego,
   * wolnego odpytania WFS GUGiK).
   */
  exportParcels: (intakeId: number, format: 'csv' | 'xlsx',
                  payload: { bufferM: number | null; rows: ParcelExportRow[] }) =>
    downloadPost(`/api/intakes/${intakeId}/parcels/export?format=${format}`, payload,
      `Dzialki-EGiB.${format}`),
};

/** Działka zapisana przy ujęciu — zwracana przez /api/intakes/{id}/saved-parcels. */
export interface SavedParcelDto {
  id: number;
  egibId: string;
  parcelNumber: string | null;
  obrebNumer: string | null;
  obrebNazwa: string | null;
  gminaNazwa: string | null;
  poleM2: number | null;
  klasouzytki: string | null;
  dataEgib: string | null;
  addedAt: string | null;
  source: string;
  geometry: { type: 'Polygon' | 'MultiPolygon'; coordinates: unknown } | null;
}

export interface SavedParcelInput {
  egibId: string;
  parcelNumber?: string | null;
  obrebNumer?: string | null;
  obrebNazwa?: string | null;
  gminaNazwa?: string | null;
  poleM2?: number | null;
  klasouzytki?: string | null;
  dataEgib?: string | null;
  geometry?: { type: 'Polygon' | 'MultiPolygon'; coordinates: unknown };
}

export const savedParcels = {
  list: (intakeId: number) =>
    api.get<SavedParcelDto[]>(`/api/intakes/${intakeId}/saved-parcels`),
  add: (intakeId: number, parcel: SavedParcelInput) =>
    api.post<SavedParcelDto>(`/api/intakes/${intakeId}/saved-parcels`, parcel),
  bulkAdd: (intakeId: number, parcels: SavedParcelInput[]) =>
    api.post<{ added: number; total: number }>(`/api/intakes/${intakeId}/saved-parcels/bulk`, parcels),
  remove: (intakeId: number, egibId: string) =>
    api.del<{ ok: boolean }>(`/api/intakes/${intakeId}/saved-parcels?egibId=${encodeURIComponent(egibId)}`),
};

export interface LocalGeomLayerDto {
  id: number;
  code: string;
  displayName: string;
  sourceUrl: string;
  sourceQueryFilter: string | null;
  sourceVersion: string | null;
  fetchedAt: string | null;
  featureCount: number;
  fillColor: string;
  fillOpacity: number;
  strokeColor: string;
  strokeWidth: number;
  refreshInProgress: boolean;
  refreshStartedAt: string | null;
  refreshError: string | null;
}

export const localGeomLayers = {
  list: () => api.get<LocalGeomLayerDto[]>('/api/admin/local-geom-layers'),
  refresh: (code: string) => api.post<LocalGeomLayerDto>(`/api/admin/local-geom-layers/${code}/refresh`),
  refreshAround: (code: string, radiusM = 10000) =>
    api.post<unknown>(`/api/admin/local-geom-layers/${code}/refresh-around?radiusM=${radiusM}`),
  /** #4 BDOT10k: jeden przebieg pobiera paczki GML per powiat i zasila wszystkie warstwy BDOT_*. */
  refreshBdot10k: (radiusM = 3000) =>
    api.post<BdotIngestSummary>(`/api/admin/local-geom-layers/bdot10k/refresh?radiusM=${radiusM}`),
};

export interface BdotIngestSummary {
  intakesResolved: number; powiatyDownloaded: number; featuresImported: number;
  perCode: Record<string, number>; unresolvedCounties: string[]; error: string | null;
}

export const mapy = {
  layers: () => api.get<MapLayer[]>('/api/mapy/layers'),
  availability: () => api.get<Record<string, boolean>>('/api/mapy/availability'),
  createLayer: (data: MapLayerInput) => api.post<MapLayer>('/api/mapy/layers', data),
  updateLayer: (id: number, data: Partial<MapLayerInput>) => api.put<MapLayer>(`/api/mapy/layers/${id}`, data),
  deleteLayer: (id: number) => api.del<{ ok: boolean }>(`/api/mapy/layers/${id}`),

  /**
   * Generuje mapę z progresywnymi eventami SSE i triggeruje download.
   * onPhase otrzymuje każdą fazę po kolei (walidacja → bbox → wms 1/N → ... → upload).
   */
  fetchWithProgress: async (req: MapFetchRequest, onPhase: (p: MapPhaseUpdate) => void): Promise<MapFetchResult> => {
    const done = await strumienZWynikiem<MapFetchResult>('/api/mapy/fetch/stream', {
      phase: json<MapPhaseUpdate>(onPhase),
    }, { body: req, kontekst: 'Generowanie mapy' });

    // Pobranie wygenerowanego pliku z proxy (MinIO/in-memory)
    const fileR = await fetch(`/api/mapy/files?key=${encodeURIComponent(done.fileKey)}&filename=${encodeURIComponent(done.filename)}`,
        { credentials: 'include' });
    if (!fileR.ok) {
      throw new Error(`Pobranie pliku nie powiodło się (${fileR.status})`);
    }
    const blob = await fileR.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = done.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);

    return {
      fileKey: done.fileKey,
      filename: done.filename,
      sizeBytes: done.sizeBytes,
      storageUrl: done.storageUrl ?? '',
    };
  },
};

export interface StudyTypeRequiredLayerInput {
  studyType: string;
  mapLayerId: number;
  mandatory?: boolean;
  note?: string | null;
  sortOrder?: number;
  active?: boolean;
}

/** Słownik warstw WMS/WMTS wymaganych per typ opracowania. */
export const studyTypeRequiredLayers = {
  list: (studyType?: string, includeInactive?: boolean) => {
    const p = new URLSearchParams();
    if (studyType) p.set('studyType', studyType);
    if (includeInactive) p.set('includeInactive', 'true');
    const qs = p.toString();
    return api.get<StudyTypeRequiredLayer[]>(`/api/study-type-required-layers${qs ? '?' + qs : ''}`);
  },
  forStudy: (studyId: number) =>
    api.get<StudyTypeRequiredLayer[]>(`/api/study-type-required-layers/for-study/${studyId}`),
  create: (data: StudyTypeRequiredLayerInput) =>
    api.post<StudyTypeRequiredLayer>('/api/study-type-required-layers', data),
  update: (id: number, data: Partial<StudyTypeRequiredLayerInput>) =>
    api.put<StudyTypeRequiredLayer>(`/api/study-type-required-layers/${id}`, data),
  delete: (id: number) =>
    api.del<{ ok: boolean; deactivated: boolean }>(`/api/study-type-required-layers/${id}`),
};

export interface ArchiveIngestResult {
  documentId: number | null;
  name: string;
  extractedChars: number;
  extractionMethod: string;
  fragmentsCreated: number;
  /** True gdy backend wykrył duplikat (ten sam SHA-256 w zakresie) i zwrócił istniejący wpis. */
  duplicate?: boolean;
}

export interface RagSearchHit {
  fragmentId: number;
  documentId: number | null;
  documentName: string | null;
  fragmentNumber: number;
  snippet: string;
  score: number;
  embeddingModel: string;
}

export interface ReindexResult {
  documentId: number | null;
  name: string | null;
  success: boolean;
  extractedChars: number;
  extractionMethod: string;
  fragmentsCreated: number;
  error: string | null;
}

export interface BulkReindexSummary {
  total: number;
  success: number;
  failed: number;
  totalFragments: number;
  results: ReindexResult[];
}

export interface ArchiveDocumentListItem {
  id: number;
  name: string;
  studyType: string | null;
  studyTypeName: string | null;
  studyTypeAbbr: string | null;
  intakeId: number | null;
  intakeName: string | null;
  clientId: number | null;
  clientName: string | null;
  sizeBytes: number | null;
  indexed: boolean;
  indexedAt: string | null;
  fragmentsCount: number;
  contentSha256: string | null;
  /** Czy plik jest dostępny dla AI-draftów (RAG search). FALSE = jest w archiwum ale RAG go pomija. */
  ragEnabled: boolean;
  /** Soft-delete: gdy != null, plik jest oznaczony do hard-delete w dniu deleteAfter. */
  deletionRequestedAt: string | null;
  deletionRequestedByName: string | null;
  deleteAfter: string | null;  // YYYY-MM-DD
  createdAt: string | null;
  /** Kategoria dokumentu (dowód/polisa/badanie…) — głównie dla załączników pojazdu. */
  docCategory: DocumentCategory | null;
  docCategoryName: string | null;
  boreholeId: number | null;
  boreholeDesignation: string | null;
  /** Skąd plik przyszedł, np. „Karta otworu · oferta OF-2026-012"; null = zwykły upload. */
  sourceLabel: string | null;
}

export interface ArchiveBackfillResult {
  updated: number;
  totalNullBefore: number;
  totalNullAfter: number;
}

export const archive = {
  upload: (file: File, opts?: { clientId?: number; intakeId?: number; year?: number }, onProgress?: (p: UploadProgress) => void) => {
    const fd = new FormData();
    fd.append('file', file);
    if (opts?.clientId) fd.append('clientId', String(opts.clientId));
    if (opts?.intakeId) fd.append('intakeId', String(opts.intakeId));
    if (opts?.year) fd.append('year', String(opts.year));
    return xhrUpload<ArchiveIngestResult>('/api/archive/upload', fd, { onProgress });
  },
  uploadBulk: (files: File[], opts?: { clientId?: number; intakeId?: number; year?: number }, onProgress?: (p: UploadProgress) => void) => {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    if (opts?.clientId) fd.append('clientId', String(opts.clientId));
    if (opts?.intakeId) fd.append('intakeId', String(opts.intakeId));
    if (opts?.year) fd.append('year', String(opts.year));
    return xhrUpload<ArchiveIngestResult[]>('/api/archive/upload/bulk', fd, { onProgress });
  },
  reindex: (id: number) => api.post<ReindexResult>(`/api/archive/${id}/reindex`),
  reindexAll: () => api.post<BulkReindexSummary>('/api/archive/reindex-all'),
  /** Indeksuje TYLKO dokumenty jeszcze nieindeksowane — tańsze od reindexAll. */
  reindexPending: () => api.post<BulkReindexSummary>('/api/archive/reindex-pending'),
  list: (filters?: { studyType?: string | null; indexed?: boolean | null;
                     intakeId?: number | null; clientId?: number | null }) => {
    const p = new URLSearchParams();
    if (filters?.studyType) p.set('studyType', filters.studyType);
    if (filters?.indexed != null) p.set('indexed', String(filters.indexed));
    if (filters?.intakeId) p.set('intakeId', String(filters.intakeId));
    if (filters?.clientId) p.set('clientId', String(filters.clientId));
    const qs = p.toString();
    return api.get<ArchiveDocumentListItem[]>('/api/archive/documents' + (qs ? '?' + qs : ''));
  },
  /**
   * Soft-delete: oznacza plik do usunięcia z 7-dniowym oknem na cofnięcie.
   * Hard-delete robi scheduled job po przekroczeniu delete_after. RAG search od razu pomija.
   */
  requestDelete: (id: number, graceDays = 7) =>
    api.del<{ ok: boolean; id?: number; name?: string; deleteAfter?: string;
              graceDays?: number; alreadyPending?: boolean }>(
      `/api/archive/documents/${id}?graceDays=${graceDays}`),
  cancelDelete: (id: number) =>
    api.post<ArchiveDocumentListItem>(`/api/archive/documents/${id}/cancel-delete`),
  /** Natychmiastowe hard-delete — admin only, omija grace period. */
  forceDelete: (id: number) =>
    api.del<{ ok: boolean; id: number; name: string; fragmentsRemoved: number; fileRemoved: boolean }>(
      `/api/archive/documents/${id}/force`),
  /** Zwraca aktualny grace period z konfiguracji systemu (klucz `archive.soft-delete.grace-days`). */
  softDeleteConfig: () => api.get<{ graceDays: number }>(`/api/archive/soft-delete-config`),
  backfillStudyTypes: () =>
    api.post<ArchiveBackfillResult>('/api/archive/backfill-study-types'),

  filesForIntake: (intakeId: number) =>
    api.get<ArchiveDocumentListItem[]>(`/api/archive/intakes/${intakeId}/files`),
  uploadForIntake: (intakeId: number, file: File, opts?: { ragEnabled?: boolean }, onProgress?: (p: UploadProgress) => void) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('ragEnabled', String(opts?.ragEnabled !== false));
    return xhrUpload<ArchiveIngestResult>(`/api/archive/intakes/${intakeId}/files`, fd, { onProgress });
  },
  filesForClient: (clientId: number) =>
    api.get<ArchiveDocumentListItem[]>(`/api/archive/clients/${clientId}/files`),
  uploadForClient: (clientId: number, file: File, opts?: { ragEnabled?: boolean }, onProgress?: (p: UploadProgress) => void) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('ragEnabled', String(opts?.ragEnabled !== false));
    return xhrUpload<ArchiveIngestResult>(`/api/archive/clients/${clientId}/files`, fd, { onProgress });
  },
  /** Załączniki referencyjne otworu (zdjęcia, szkice). Domyślnie bez indeksacji RAG. */
  filesForBorehole: (boreholeId: number) =>
    api.get<ArchiveDocumentListItem[]>(`/api/boreholes/${boreholeId}/files`),
  uploadForBorehole: (boreholeId: number, file: File, opts?: { ragEnabled?: boolean }, onProgress?: (p: UploadProgress) => void) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('ragEnabled', String(opts?.ragEnabled === true));
    return xhrUpload<ArchiveIngestResult>(`/api/boreholes/${boreholeId}/files`, fd, { onProgress });
  },
  setRagEnabled: (id: number, ragEnabled: boolean) =>
    api.put<ArchiveDocumentListItem>(`/api/archive/documents/${id}/rag-enabled`, { ragEnabled }),

  filesForEmployee: (employeeId: number) =>
    api.get<ArchiveDocumentListItem[]>(`/api/employees/${employeeId}/files`),
  uploadForEmployee: (employeeId: number, file: File, opts?: { ragEnabled?: boolean }, onProgress?: (p: UploadProgress) => void) =>
    uploadMultipart(`/api/employees/${employeeId}/files`, file, opts, onProgress),

  filesForVehicle: (vehicleId: number) =>
    api.get<ArchiveDocumentListItem[]>(`/api/vehicles/${vehicleId}/files`),
  uploadForVehicle: (vehicleId: number, file: File, opts?: { ragEnabled?: boolean; category?: DocumentCategory | null }, onProgress?: (p: UploadProgress) => void) =>
    uploadMultipart(`/api/vehicles/${vehicleId}/files`, file, opts, onProgress),
  /** Skany faktur (V153) — backend wymusza ragEnabled=false, faktury nie trafiają do korpusów AI. */
  filesForInvoice: (invoiceId: number) =>
    api.get<ArchiveDocumentListItem[]>(`/api/invoices/${invoiceId}/files`),
  uploadForInvoice: (invoiceId: number, file: File, opts?: { ragEnabled?: boolean; category?: DocumentCategory | null }, onProgress?: (p: UploadProgress) => void) =>
    uploadMultipart(`/api/invoices/${invoiceId}/files`, file, opts, onProgress),
};

/** Wspólny helper uploadu multipart (pliki pracownika/pojazdu). */
function uploadMultipart(path: string, file: File, opts?: { ragEnabled?: boolean; category?: DocumentCategory | null }, onProgress?: (p: UploadProgress) => void) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('ragEnabled', String(opts?.ragEnabled === true));
  if (opts?.category) fd.append('category', opts.category);
  return xhrUpload<ArchiveIngestResult>(path, fd, { onProgress });
}

// === Załączniki opracowania (study_attachment) =====================================

export type StudyAttachmentCategory =
  | 'RESPONSE_AUTHORITY' | 'DECISION' | 'REJECTION' | 'CORRESPONDENCE' | 'OTHER';

export interface StudyAttachmentDto {
  id: number;
  archiveDocumentId: number | null;
  filename: string | null;
  sizeBytes: number | null;
  category: StudyAttachmentCategory;
  categoryName: string;
  description: string | null;
  receivedAt: string | null;
  createdAt: string | null;
  createdByName: string | null;
}

export interface StudyAttachmentCategoryOption {
  code: StudyAttachmentCategory;
  displayName: string;
}

export const studyAttachments = {
  categories: () =>
    api.get<StudyAttachmentCategoryOption[]>('/api/study-attachments/categories'),
  list: (studyId: number) =>
    api.get<StudyAttachmentDto[]>(`/api/studies/${studyId}/attachments`),
  /** Pliki z oferty, z której powstało opracowanie (zapytanie, karta otworu…) — tylko odczyt. */
  fromOffer: (studyId: number) =>
    api.get<OfferAttachmentDto[]>(`/api/studies/${studyId}/offer-attachments`),
  upload: (studyId: number, file: File, opts: {
    category: StudyAttachmentCategory;
    description?: string;
    receivedAt?: string;  // YYYY-MM-DD
  }, onProgress?: (p: UploadProgress) => void) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', opts.category);
    if (opts.description) fd.append('description', opts.description);
    if (opts.receivedAt) fd.append('receivedAt', opts.receivedAt);
    return xhrUpload<StudyAttachmentDto>(`/api/studies/${studyId}/attachments`, fd, { onProgress });
  },
  updateMetadata: (studyId: number, attachmentId: number, body: {
    category?: StudyAttachmentCategory;
    description?: string | null;
    receivedAt?: string | null;
  }) =>
    api.put<StudyAttachmentDto>(
      `/api/studies/${studyId}/attachments/${attachmentId}`, body),
  remove: (studyId: number, attachmentId: number) =>
    api.del<{ ok: boolean; attachmentId: number; filename: string }>(
      `/api/studies/${studyId}/attachments/${attachmentId}`),
};

export const rag = {
  search: (query: string, k = 5) =>
    api.post<RagSearchHit[]>('/api/rag/search', { query, k }),
};

export interface DriveRemoteFile {
  id: string;
  name: string;
  mimeType: string;
  size: number | null;
  modifiedTime: string | null;
  parentFolderId: string | null;
  folder: boolean;
  webViewLink: string | null;
}

export const drive = {
  files: (clientId?: number) => {
    const qs = clientId ? `?clientId=${clientId}` : '';
    return api.get<ArchiveDocument[]>('/api/drive/files' + qs);
  },
  stats: () => api.get<Record<string, unknown>>('/api/drive/stats'),
  clients: () => api.get<Client[]>('/api/drive/clients'),
  reindex: (id: number) => api.post<{ id: number; indexed: boolean }>(`/api/drive/reindex/${id}`),
  batchImport: (ids: number[]) => api.post<{ zindeksowano: number; total: number }>('/api/drive/batch-import', ids),
  listRemote: (query?: string, folderId?: string, pageSize = 100) => {
    const p = new URLSearchParams();
    if (query) p.set('query', query);
    if (folderId) p.set('folderId', folderId);
    p.set('pageSize', String(pageSize));
    return api.get<DriveRemoteFile[]>('/api/drive/remote/list?' + p);
  },
  importRemote: (data: { fileId: string; name: string; mimeType: string; clientId?: number; year?: number }) =>
    api.post<ArchiveIngestResult>('/api/drive/remote/import', data),
  importAll: (data: { rootFolderId?: string; defaultClientId?: number; defaultYear?: number; maxFiles?: number }) =>
    api.post<{
      totalFound: number; imported: number; skipped: number; failed: number; totalFragments: number;
      details: { fileId: string; name: string; mimeType: string; status: string; fragments: number; error: string | null }[];
    }>('/api/drive/remote/import-all', data),
  importAllStart: (data: { rootFolderId?: string; defaultClientId?: number; defaultYear?: number; maxFiles?: number }) =>
    api.post<{ jobId: string; ok: boolean }>('/api/drive/remote/import-all/start', data),
  importAllStatus: (jobId: string) =>
    api.get<{
      found: boolean;
      jobId: string;
      state: 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED' | 'CANCELLED';
      stage: string;
      totalFound: number;
      processed: number;
      imported: number;
      skipped: number;
      failed: number;
      totalFragments: number;
      currentFile: string | null;
      startedAt: string;
      finishedAt: string | null;
      error: string | null;
      details?: { fileId: string; name: string; mimeType: string; status: string; fragments: number; error: string | null }[];
    }>(`/api/drive/remote/import-all/status?jobId=${encodeURIComponent(jobId)}`),
  importAllCancel: (jobId: string) =>
    api.post<{ ok: boolean; jobId: string }>(`/api/drive/remote/import-all/cancel?jobId=${encodeURIComponent(jobId)}`),
};

export const audit = {
  log: (type?: string, limit = 200) => {
    const p = new URLSearchParams();
    if (type) p.set('type', type);
    p.set('limit', String(limit));
    return api.get<AuditEntry[]>('/api/audit/log?' + p);
  },
  verify: () => api.get<{ ok: boolean; count: number; dzisiaj: number }>('/api/audit/verify'),
  types: () => api.get<string[]>('/api/audit/types'),
};

export interface BundleManifestEntry {
  zipPath: string;
  displayName: string;
  category: 'WNIOSEK_DOCX' | 'OPERAT_PDF' | 'OPERAT_DOCX' | 'GRAPHICAL' | 'TEXTUAL' | 'README';
  parentReqName: string | null;
  sizeBytes: number | null;
  archiveDocumentId: number | null;
  downloadUrl: string | null;
}

export interface BundleManifest {
  zipFilename: string;
  entries: BundleManifestEntry[];
  graphicalCount: number;
  textualCount: number;
  missingFiles: number;
}

export const pdf = {
  generate: (studyId: number) =>
    api.post<{ id: number; fileName: string; sizeBytes: number; sha256: string; retentionUntil: string }>(
      `/api/pdf/generate/${studyId}`),
  podgladUrl: (studyId: number) => `/api/pdf/preview/${studyId}`,
  bundleManifest: (studyId: number) =>
    api.get<BundleManifest>(`/api/pdf/${studyId}/bundle-manifest`),
};

export const totp = {
  status: () => api.get<{ enabled: boolean; wymagane: boolean; backupCodesCount: number }>('/api/2fa/status'),
  startEnroll: () => api.post<{ secret: string; qrImage: string; backupCodes: string[] }>('/api/2fa/enroll/start'),
  confirmEnroll: (secret: string, code: string, backupCodes: string[]) =>
    api.post<{ ok: boolean; enabled: boolean }>('/api/2fa/enroll/confirm', { secret, code, backupCodes }),
  /** Wyłączenie wymaga aktualnego kodu (TOTP albo zapasowego). */
  disable: (code: string) => api.post<{ ok: boolean; enabled: boolean }>('/api/2fa/disable', { code }),
  verify: (code: string) => api.post<{ ok: boolean }>('/api/2fa/verify', { code }),
};

export interface ConfigEntry {
  key: string;
  category: string;
  description?: string;
  secret: boolean;
  ustawiony: boolean;
  value: string;
  modifiedBy?: string;
  updatedAt?: string;
}

/** Metadane certyfikatu KSeF (z GET /api/admin/config/ksef/certificate). */
export interface KsefCertInfo {
  set: boolean;
  subject: string | null;
  issuer: string | null;
  serialNumber: string | null;
  validFrom: string | null;
  validTo: string | null;
  expired: boolean;
}

export interface UserAdmin {
  id: number;
  login: string;
  firstName: string;
  lastName: string;
  fullName: string;
  initials: string;
  email?: string;
  roles: string[];
  category: string;
  certificateNumber?: string;
  /** Typy opracowań (kody StudyType), które wykonuje — tablica pokazuje mu zadania tych typów do przyjęcia. */
  studyTypes?: string[];
  active: boolean;
  totpEnabled: boolean;
  passwordChangeRequired: boolean;
  lastLogin?: string;
  lockedAt?: string;
  lockReason?: string;
  /**
   * Czy zalogowany może administrować tym kontem (reset hasła, blokada, edycja). Serwer liczy
   * to z porównania przywilejów — konto o wyższych uprawnieniach jest poza zasięgiem, żeby
   * ADMIN_USERS nie wystarczał do przejęcia konta właściciela.
   */
  zarzadzalne: boolean;
}

export interface RoleInfo {
  name: string;          // kod roli
  displayName: string;
  system: boolean;       // rola wbudowana (nie do usunięcia)
  globalViewer: boolean; // widzi wszystkie opracowania bez przypisania
  active: boolean;
}

export interface PermissionsMatrix {
  role: RoleInfo[];
  permissions: { name: string; description: string; category: string }[];
  matryca: Record<string, Record<string, boolean>>;
}

export interface ProjectMember {
  id: number;
  userId: number;
  login: string;
  fullName: string;
  initials: string;
  category: string;
  accessLevel: 'VIEW' | 'EDIT' | 'SIGN' | 'MANAGE';
  note?: string;
  assignedBy?: string;
  createdAt?: string;
}

export const admin = {
  users: {
    list: () => api.get<UserAdmin[]>('/api/admin/users'),
    get: (id: number) => api.get<UserAdmin>(`/api/admin/users/${id}`),
    create: (data: {
      login: string; firstName: string; lastName: string; email?: string;
      roles: string[]; category: string; certificateNumber?: string; studyTypes?: string[];
      password: string; wymagajZmianyHasla?: boolean;
    }) => api.post<UserAdmin>('/api/admin/users', data),
    update: (id: number, data: {
      firstName?: string; lastName?: string; email?: string;
      roles?: string[]; category?: string; certificateNumber?: string; studyTypes?: string[];
    }) => api.put<UserAdmin>(`/api/admin/users/${id}`, data),
    // Ścieżki po angielsku — zgodnie z UserAdminApi i resztą API. Wcześniej stały tu polskie
    // (/blokuj, /odblokuj, /reset-hasla), których backend nigdy nie mapował, więc wszystkie
    // trzy akcje cicho leciały w 404.
    block: (id: number, powod: string) =>
      api.post<UserAdmin>(`/api/admin/users/${id}/lock`, { powod }),
    unblock: (id: number) => api.post<UserAdmin>(`/api/admin/users/${id}/unlock`),
    resetPassword: (id: number) =>
      api.post<{ ok: boolean; password: string; uwaga: string }>(`/api/admin/users/${id}/reset-password`),
  },
  permissions: {
    matrix: () => api.get<PermissionsMatrix>('/api/admin/permissions/matrix'),
    set: (role: string, permission: string, allowed: boolean) =>
      api.put<{ ok: boolean }>('/api/admin/permissions/matrix', { role, permission, allowed }),
    bulk: (zmiany: { role: string; permission: string; allowed: boolean }[]) =>
      api.put<{ ok: boolean; zaktualizowane: number }>('/api/admin/permissions/matrix/bulk', { zmiany }),
    // Katalog ról (dynamiczne role) — administrator może tworzyć własne role.
    roles: () => api.get<RoleInfo[]>('/api/admin/permissions/roles'),
    createRole: (body: { code: string; displayName: string; globalViewer?: boolean }) =>
      api.post<RoleInfo>('/api/admin/permissions/roles', body),
    updateRole: (code: string, body: { displayName?: string; globalViewer?: boolean; active?: boolean }) =>
      api.put<RoleInfo>(`/api/admin/permissions/roles/${encodeURIComponent(code)}`, body),
    removeRole: (code: string) =>
      api.del<{ ok: boolean }>(`/api/admin/permissions/roles/${encodeURIComponent(code)}`),
  },
  config: {
    list: (category?: string) => {
      const qs = category ? '?category=' + category : '';
      return api.get<ConfigEntry[]>('/api/admin/config' + qs);
    },
    set: (key: string, value: string) =>
      api.put<{ ok: boolean; skipped?: boolean }>('/api/admin/config', { key, value }),
    bulk: (zmiany: { key: string; value: string }[]) =>
      api.put<{ ok: boolean; zaktualizowane: number; pominiete: number }>('/api/admin/config/bulk', { zmiany }),
    testMail: (to: string) =>
      api.post<Record<string, unknown>>('/api/admin/config/mail/test', { to }),
    testClaude: () => api.post<Record<string, unknown>>('/api/admin/config/claude/test'),
    testGemini: () => api.post<Record<string, unknown>>('/api/admin/config/gemini/test'),
    /** Test połączenia z dostawcą GPS floty — pyta dostawcę na żywo, z pominięciem bufora. */
    testGps: () => api.post<Record<string, unknown>>('/api/admin/config/gps/test'),
    aiCatalog: () => api.get<{
      tasks: { code: string; name: string; description: string }[];
      providers: string[];
      modelsByProvider: Record<string, string[]>;
    }>('/api/admin/config/ai/catalog'),
    discoverModels: (provider: string) => api.get<{
      provider: string;
      fetchedAt: string;
      cached: boolean;
      error: string | null;
      models: {
        id: string;
        displayName: string;
        description: string;
        inputTokenLimit: number | null;
        outputTokenLimit: number | null;
        capabilities: string[];
      }[];
    }>(`/api/admin/config/ai/models/discover?provider=${provider}`),
    flushDiscoveryCache: () =>
      api.post<{ ok: boolean; message: string }>('/api/admin/config/ai/models/discover/cache-flush'),
    listModels: (provider?: string, capability?: string) => {
      const p = new URLSearchParams();
      if (provider) p.set('provider', provider);
      if (capability) p.set('capability', capability);
      const qs = p.toString();
      return api.get<{
        id: number;
        provider: string;
        modelId: string;
        displayName: string;
        description: string | null;
        inputTokenLimit: number | null;
        outputTokenLimit: number | null;
        capabilities: string[];
        manual: boolean;
        active: boolean;
        lastSeenAt: string | null;
      }[]>('/api/admin/config/ai/models' + (qs ? '?' + qs : ''));
    },
    testDrive: () => api.post<Record<string, unknown>>('/api/admin/config/drive/test'),
    /** Metadane aktualnie zapisanego certyfikatu KSeF (bez materiału tajnego). */
    ksefCertInfo: () => api.get<KsefCertInfo>('/api/admin/config/ksef/certificate'),
    /**
     * Wgranie certyfikatu KSeF przez załączenie plików. Przekaż `key`+`crt` (osobne pliki PEM/DER)
     * albo `p12` (gotowy pakiet PKCS#12), zawsze z hasłem. Backend buduje/waliduje keystore i zapisuje.
     */
    uploadKsefCertificate: async (files: { key?: File; crt?: File; p12?: File; password: string })
        : Promise<{ ok: boolean; certificate?: KsefCertInfo; error?: string }> => {
      const fd = new FormData();
      if (files.key) fd.append('key', files.key);
      if (files.crt) fd.append('crt', files.crt);
      if (files.p12) fd.append('p12', files.p12);
      fd.append('password', files.password);
      const res = await fetch('/api/admin/config/ksef/certificate', {
        method: 'POST', credentials: 'include', body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new ApiError(res.status, body?.error ?? body?.message ?? res.statusText);
      return body;
    },
  },
  tokenUsage: {
    summary: () => api.get<{
      byTime: Record<string, Record<string, number | string>>;
      perModel: Record<string, unknown>[];
      perStep: Record<string, unknown>[];
      perStudy: Record<string, unknown>[];
    }>('/api/admin/token-usage/summary'),
    perStudy: () => api.get<Record<string, unknown>[]>('/api/admin/token-usage/per-study'),
    perStep: () => api.get<Record<string, unknown>[]>('/api/admin/token-usage/per-step'),
    perModel: () => api.get<Record<string, unknown>[]>('/api/admin/token-usage/per-model'),
    historiaStudies: (id: number) =>
      api.get<Record<string, unknown>[]>(`/api/admin/token-usage/study/${id}/history`),
  },
  tasks: {
    summary: () => api.get<TaskAdminSummary>('/api/admin/tasks/summary'),
    list: (params?: { userId?: number; status?: string; overdueOnly?: boolean; unassigned?: boolean }) => {
      const p = new URLSearchParams();
      if (params?.userId) p.set('userId', String(params.userId));
      if (params?.status) p.set('status', params.status);
      if (params?.overdueOnly) p.set('overdueOnly', 'true');
      if (params?.unassigned) p.set('unassigned', 'true');
      const qs = p.toString();
      return api.get<Task[]>('/api/admin/tasks' + (qs ? '?' + qs : ''));
    },
    workflowSummary: () => api.get<WorkflowSummary>('/api/admin/tasks/workflow/summary'),
  },
};

export interface WorkflowStepRow {
  stepId: number;
  studyId: number | null;
  studyNumber: string | null;
  studyTitle: string | null;
  stepCode: string;
  stepName: string;
  stepNumber: number;
  status: 'TODO' | 'IN_PROGRESS' | 'COMPLETED' | 'SKIPPED';
  assigneeId: number | null;
  assigneeFullName: string | null;
  assigneeInitials: string | null;
  startedAt: string | null;
  studyDeadline: string | null;
  ageHours: number;
}

export interface WorkflowUserSummary {
  userId: number;
  fullName: string;
  initials: string;
  totalActive: number;
  inProgress: number;
  todo: number;
  distinctStudies: number;
  oldestActiveHours: number | null;
  oldestStudyNumber: string | null;
  oldestStepCode: string | null;
}

export interface WorkflowSummary {
  totalActiveSteps: number;
  totalInProgress: number;
  totalTodo: number;
  unassignedActive: number;
  byUser: WorkflowUserSummary[];
  rows: WorkflowStepRow[];
}

export interface TaskStatusCounts {
  todo: number;
  inProgress: number;
  aiVerification: number;
  humanVerification: number;
  completed: number;
  open: number;
  total: number;
}

export interface TaskUserSummary {
  userId: number;
  fullName: string;
  email: string | null;
  initials: string;
  roles: string[];
  active: boolean;
  counts: TaskStatusCounts;
  overdueCount: number;
  avgOpenAgeHours: number | null;
  oldestOpenAgeHours: number | null;
  oldestOpenTaskTitle: string | null;
}

export interface TaskAdminSummary {
  counts: TaskStatusCounts;
  overdueCount: number;
  activeUsers: number;
  unassignedOpen: number;
  byUser: TaskUserSummary[];
}

export const projectAssignments = {
  list: (studyId: number) =>
    api.get<ProjectMember[]>(`/api/studies/${studyId}/zespol`),
  add: (studyId: number, userId: number, accessLevel: string, note?: string) =>
    api.post<{ ok: boolean; id: number }>(`/api/studies/${studyId}/zespol`, {
      userId, accessLevel, note,
    }),
  remove: (studyId: number, userId: number) =>
    api.del<{ ok: boolean }>(`/api/studies/${studyId}/zespol/${userId}`),
};

export const rodo = {
  eksportClient: (id: number) => api.get<Record<string, unknown>>(`/api/rodo/client/${id}/eksport`),
  eksportUser: (id: number) => api.get<Record<string, unknown>>(`/api/rodo/user/${id}/eksport`),
  zgloszenieUsuniecia: (id: number, powod: string) =>
    api.post<Record<string, unknown>>(`/api/rodo/client/${id}/usun`, { powod }),
};

// ============= Cennik per typ opracowania =============

export interface StudyTypePrice {
  id: number;
  studyType: string;            // np. 'OP_WOD_3B'
  studyTypeName: string;
  studyTypeAbbreviation: string;
  variant: string;              // np. 'SIMPLE', 'VC_PUMPING'
  unit: string;
  defaultQuantity: number;
  unitPriceNet: number;
  description: string | null;
  sortOrder: number;
  active: boolean;
}

export interface StudyTypePriceInput {
  studyType: string;
  variant: string;
  unit?: string;
  defaultQuantity?: number;
  unitPriceNet: number;
  description?: string | null;
  sortOrder?: number;
  active?: boolean;
}

export const studyTypePrices = {
  list: (includeInactive?: boolean) =>
    api.get<StudyTypePrice[]>(
      '/api/study-type-prices' + (includeInactive ? '?includeInactive=true' : '')),
  create: (data: StudyTypePriceInput) =>
    api.post<StudyTypePrice>('/api/study-type-prices', data),
  update: (id: number, data: Partial<StudyTypePriceInput>) =>
    api.put<StudyTypePrice>(`/api/study-type-prices/${id}`, data),
  delete: (id: number) =>
    api.del<{ ok: boolean; deactivated: boolean }>(`/api/study-type-prices/${id}`),
};

export interface StudyVariantItem {
  id: number;
  studyType: string;
  studyTypeName: string;
  variant: string;
  description: string;
  unit: string;
  defaultQuantity: number;
  unitPriceNet: number;
  sortOrder: number;
  active: boolean;
}

export interface StudyVariantItemInput {
  studyType: string;
  variant: string;
  description: string;
  unit?: string;
  defaultQuantity?: number;
  unitPriceNet: number;
  sortOrder?: number;
  active?: boolean;
}

/** Słownik podpowiadanych pozycji kosztorysu per (typ opracowania × wariant). */
export const studyVariantItems = {
  /** Podpowiadane (aktywne) pozycje dla wybranego typu+wariantu — do kreatora oferty. */
  suggest: (studyType: string, variant: string) =>
    api.get<StudyVariantItem[]>(
      `/api/study-variant-items/suggest?studyType=${encodeURIComponent(studyType)}&variant=${encodeURIComponent(variant)}`),
  list: (studyType?: string, variant?: string) => {
    const p = new URLSearchParams();
    if (studyType) p.set('studyType', studyType);
    if (variant) p.set('variant', variant);
    const qs = p.toString();
    return api.get<StudyVariantItem[]>('/api/study-variant-items' + (qs ? '?' + qs : ''));
  },
  create: (data: StudyVariantItemInput) =>
    api.post<StudyVariantItem>('/api/study-variant-items', data),
  update: (id: number, data: Partial<StudyVariantItemInput>) =>
    api.put<StudyVariantItem>(`/api/study-variant-items/${id}`, data),
  delete: (id: number) =>
    api.del<{ ok: boolean; deactivated: boolean }>(`/api/study-variant-items/${id}`),
};

// ============= Bulk-import katalogu + AI klasyfikacja =============

export type ProposalStatus = 'PROCESSING' | 'PROPOSED' | 'ACCEPTED' | 'REJECTED';

export interface BulkProposal {
  id: number;
  studyId: number;
  archiveDocumentId: number;
  archiveDocumentName: string;
  archiveSizeBytes: number | null;
  proposedSrdId: number | null;
  proposedSrdCode: string | null;
  proposedSrdName: string | null;
  confidence: number | null;
  source: string | null;        // EMBEDDING / LLM / NONE
  status: ProposalStatus;
  explanation: string | null;
  alternativesJson: string | null;
  jobId: string;
}

export interface BulkJobStatus {
  jobId: string;
  total: number;
  processing: number;
  proposed: number;
  done: number;
  items: BulkProposal[];
  /** Faza serwerowa: ingesting | classifying | cancelling | done | cancelled | failed. Brak gdy job wygaszony. */
  phase?: 'ingesting' | 'classifying' | 'cancelling' | 'done' | 'cancelled' | 'failed';
  /** Liczba plików po splittingu PDF — bywa większa niż uploadowana liczba. */
  totalFiles?: number;
  /** Ile plików już ma proposal w DB (zaingestowanych). */
  ingested?: number;
  /** Plik aktualnie ingestowany (przydatne do diagnozy zawiesy na konkretnym pliku). */
  currentFilename?: string | null;
  /** Ile plików zostało pominiętych jako duplikat (ten sam SHA-256). */
  skippedDuplicates?: number;
  /** Nazwy pominiętych plików (do max 20). */
  skippedFilenames?: string[];
  /** ISO timestamp startu jobu — frontend liczy elapsed na żywo. */
  startedAt?: string;
  finishedAt?: string | null;
  error?: string;
}

export interface BulkStartResult {
  jobId: string | null;
  filesQueued: number;
  filesReceived: number;
  skippedDuplicates: number;
  skippedFilenames: string[];
  warning?: string;
}

export const bulkClassification = {
  /**
   * Upload z progresem (XHR — `fetch` nie wspiera onUploadProgress).
   * @param onProgress wywołane z {loaded, total, percent} podczas wysyłania.
   * @returns obiekt z metodą abort() do anulowania w trakcie wysyłki.
   */
  start: (
    studyId: number,
    files: File[],
    onProgress?: (p: { loaded: number; total: number; percent: number }) => void,
  ): { promise: Promise<BulkStartResult>; abort: () => void } => {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    const xhr = new XMLHttpRequest();
    const promise = new Promise<BulkStartResult>((resolve, reject) => {
      xhr.open('POST', `/api/studies/${studyId}/bulk-classification/start`);
      xhr.withCredentials = true;
      if (onProgress && xhr.upload) {
        xhr.upload.onprogress = ev => {
          if (ev.lengthComputable) {
            onProgress({
              loaded: ev.loaded,
              total: ev.total,
              percent: Math.round((ev.loaded / ev.total) * 100),
            });
          }
        };
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { reject(new Error('Niepoprawna odpowiedź serwera')); }
        } else {
          let msg = xhr.statusText || 'Błąd serwera';
          try { msg = JSON.parse(xhr.responseText).message ?? msg; } catch { /* keep default */ }
          reject(new ApiError(xhr.status, msg));
        }
      };
      xhr.onerror = () => reject(new Error('Failed to fetch — sprawdź połączenie'));
      xhr.onabort = () => reject(new Error('Upload anulowany'));
      xhr.send(fd);
    });
    return { promise, abort: () => xhr.abort() };
  },
  proposals: (jobId: string) =>
    api.get<BulkJobStatus>(`/api/bulk-classification/jobs/${encodeURIComponent(jobId)}/proposals`),
  /**
   * Sprawdza czy dla tego opracowania jest aktywny bulk-import (running albo z propozycjami
   * czekającymi na decyzję). Pozwala wznowić podgląd po wyjściu i powrocie na stronę study.
   */
  activeForStudy: (studyId: number) =>
    api.get<{ active: boolean } & Partial<BulkJobStatus>>(
      `/api/studies/${studyId}/bulk-classification/active`),
  accept: (proposalId: number, overrideSrdId?: number | null) =>
    api.post<BulkProposal>(`/api/bulk-classification/proposals/${proposalId}/accept`,
      overrideSrdId != null ? { overrideSrdId } : {}),
  reject: (proposalId: number) =>
    api.post<BulkProposal>(`/api/bulk-classification/proposals/${proposalId}/reject`),
  /**
   * Wymusza LLM-reklasyfikację jednej propozycji (np. słabe dopasowanie embedding-a).
   * Blokuje na ~10-30s (czas LLM-calla).
   */
  reclassifyOneViaLlm: (proposalId: number) =>
    api.post<BulkProposal>(`/api/bulk-classification/proposals/${proposalId}/reclassify-llm`),
  /** Anuluje running job — pozostałe PROCESSING dostają status PROPOSED z notą. */
  cancel: (jobId: string) =>
    api.post<{ ok: boolean; jobId: string }>(`/api/bulk-classification/jobs/${encodeURIComponent(jobId)}/cancel`),
  /** Re-klasyfikuje nieobsłużone propozycje. all=true → wszystkie PROPOSED, false → tylko luźne. */
  reclassify: (jobId: string, all?: boolean) => {
    const qs = all ? '?all=true' : '';
    return api.post<{ ok: boolean; jobId: string; queued: number }>(
      `/api/bulk-classification/jobs/${encodeURIComponent(jobId)}/reclassify${qs}`);
  },
};

// ============= Wymagane dokumenty (checklist per Study) =============

export type DocumentSource = 'FIRM' | 'CLIENT';
export type RequiredDocumentStatus = 'PENDING' | 'RECEIVED' | 'NOT_APPLICABLE' | 'WAIVED';

export interface RequiredDocumentTemplate {
  id: number;
  studyType: string;          // np. 'OP_WOD_3B'
  code: string;               // np. 'DOK_HYDRO'
  name: string;
  description: string | null;
  legalBasis: string | null;
  defaultSource: DocumentSource;
  mandatory: boolean;
  conditionText: string | null;
  qThresholdM3Year: number | null;
  sortOrder: number;
  active: boolean;
}

export interface RequiredDocumentTemplateInput {
  studyType: string;
  code: string;
  name: string;
  description?: string | null;
  legalBasis?: string | null;
  defaultSource: DocumentSource;
  mandatory?: boolean;
  conditionText?: string | null;
  qThresholdM3Year?: number | null;
  sortOrder?: number;
  active?: boolean;
}

export const requiredDocumentTemplates = {
  list: (studyType?: string, includeInactive?: boolean) => {
    const p = new URLSearchParams();
    if (studyType) p.set('studyType', studyType);
    if (includeInactive) p.set('includeInactive', 'true');
    const qs = p.toString();
    return api.get<RequiredDocumentTemplate[]>('/api/required-document-templates' + (qs ? '?' + qs : ''));
  },
  create: (data: RequiredDocumentTemplateInput) =>
    api.post<RequiredDocumentTemplate>('/api/required-document-templates', data),
  update: (id: number, data: Partial<RequiredDocumentTemplateInput>) =>
    api.put<RequiredDocumentTemplate>(`/api/required-document-templates/${id}`, data),
  delete: (id: number) =>
    api.del<{ ok: boolean; deactivated: boolean }>(`/api/required-document-templates/${id}`),
};

export interface RequiredDocumentFile {
  id: number;
  name: string;
  drivePath: string | null;
  driveFileId: string | null;
  sizeBytes: number | null;
  addedAt: string;
}

export interface RequiredDocumentItem {
  id: number;
  templateId: number | null;
  code: string;
  name: string;
  description: string | null;
  legalBasis: string | null;
  source: DocumentSource;
  sourceName: string;
  mandatory: boolean;
  conditionText: string | null;
  qThresholdM3Year: number | null;
  sortOrder: number;
  status: RequiredDocumentStatus;
  statusName: string;
  note: string | null;
  dueDate: string | null;       // YYYY-MM-DD
  completedAt: string | null;
  completedByName: string | null;
  files: RequiredDocumentFile[];
}

export interface RequiredDocumentChecklist {
  studyId: number;
  studyNumber: string;
  studyType: string;
  /** Roczny pobór ujęcia (m³/rok) — null gdy żadna studnia ujęcia nie ma wpisanego avgDailyIntakeM3. */
  annualIntakeM3: number | null;
  total: number;
  received: number;
  pending: number;
  notApplicable: number;
  waived: number;
  items: RequiredDocumentItem[];
}

export const studyRequiredDocuments = {
  list: (studyId: number) =>
    api.get<RequiredDocumentChecklist>(`/api/studies/${studyId}/required-documents`),
  refresh: (studyId: number) =>
    api.post<{ added: number }>(`/api/studies/${studyId}/required-documents/refresh`),
  changeStatus: (id: number, status: RequiredDocumentStatus, note?: string | null) =>
    api.put<RequiredDocumentItem>(`/api/study-required-documents/${id}/status`, { status, note }),
  updateFields: (id: number, data: { source?: DocumentSource; note?: string | null; dueDate?: string | null }) =>
    api.put<RequiredDocumentItem>(`/api/study-required-documents/${id}`, data),
  uploadFile: (id: number, file: File, onProgress?: (p: UploadProgress) => void): Promise<{
    item: RequiredDocumentItem;
    duplicate: boolean;
    alreadyLinked: boolean;
    archiveDocumentName: string;
  }> => {
    const fd = new FormData();
    fd.append('file', file);
    return xhrUpload<{
      item: RequiredDocumentItem;
      duplicate: boolean;
      alreadyLinked: boolean;
      archiveDocumentName: string;
    }>(`/api/study-required-documents/${id}/files`, fd, { onProgress });
  },
  linkExisting: (id: number, archiveDocumentId: number) =>
    api.post<RequiredDocumentItem>(
      `/api/study-required-documents/${id}/files/${archiveDocumentId}/link`),
  unlinkFile: (id: number, archiveDocumentId: number) =>
    api.del<RequiredDocumentItem>(
      `/api/study-required-documents/${id}/files/${archiveDocumentId}`),
};

export interface ReportOfferRef {
  id: number;
  number: string;
  title: string;
  clientName: string | null;
  intakeId: number | null;
  intakeName: string | null;
  totalNet: number;
  totalGross: number;
  issueDate: string;
}

export interface ReportStatusBucket {
  count: number;
  totalNet: number;
  totalGross: number;
  offers: ReportOfferRef[];
}

export interface ReportMonthBucket {
  month: string;          // "YYYY-MM"
  count: number;
  totalNet: number;
  totalGross: number;
}

export interface OfferReport {
  from: string;
  to: string;
  studyType?: string | null;
  byStatus: Record<OfferStatus, ReportStatusBucket>;
  byMonth: ReportMonthBucket[];
  total: ReportStatusBucket;
}

// === Raport opracowań ===

export interface ReportStudyRef {
  id: number;
  number: string;
  title: string;
  studyType: string | null;
  studyTypeAbbr: string | null;
  studyTypeName: string | null;
  status: string | null;
  statusName: string | null;
  intakeId: number | null;
  intakeName: string | null;
  clientName: string | null;
  dispatchDate: string | null;
}

export interface ReportStudyTypeBucket {
  type: string;
  abbreviation: string;
  displayName: string;
  count: number;
  studies: ReportStudyRef[];
}

export interface ReportStudyMonthBucket {
  month: string;          // "YYYY-MM"
  count: number;
  countByType: Record<string, number>;  // np. {"OP_WOD_3B": 2, "DOK_HYDRO_4A": 1}
}

export interface StudyReport {
  from: string;
  to: string;
  studyType?: string | null;
  totalCount: number;
  byType: ReportStudyTypeBucket[];
  byMonth: ReportStudyMonthBucket[];
}

export const reports = {
  offers: (from: string, to: string, studyType?: string) => {
    const p = new URLSearchParams({ from, to });
    if (studyType) p.set('studyType', studyType);
    return api.get<OfferReport>(`/api/reports/offers?${p.toString()}`);
  },
  studies: (from: string, to: string, studyType?: string) => {
    const p = new URLSearchParams({ from, to });
    if (studyType) p.set('studyType', studyType);
    return api.get<StudyReport>(`/api/reports/studies?${p.toString()}`);
  },
};

// ── Analiza sitowa / dobór obsypki i szczeliny filtra (V86) ──────────────
import type {
  SieveProjectSummary, SieveProject, SieveProjectInput, SieveSampleInput,
  SieveDecisionInput, SieveExtraction, SieveMeta, SieveProjectStatus,
  SieveReportOptions, SieveReportArchiveResult,
} from '@/types';

export const sieveAnalysis = {
  /**
   * Lista projektów. Wszystkie trzy filtry są opcjonalne i składają się ze sobą;
   * po stronie backendu filtr po otworze jest węższy i ma pierwszeństwo nad filtrem po ujęciu.
   */
  list: (status?: SieveProjectStatus, intakeId?: number | null, boreholeId?: number | null) => {
    const q = new URLSearchParams();
    if (status) q.set('status', status);
    if (intakeId != null) q.set('intakeId', String(intakeId));
    if (boreholeId != null) q.set('boreholeId', String(boreholeId));
    const qs = q.toString();
    return api.get<SieveProjectSummary[]>('/api/sieve-analysis' + (qs ? `?${qs}` : ''));
  },
  get: (id: number) => api.get<SieveProject>(`/api/sieve-analysis/${id}`),
  meta: () => api.get<SieveMeta>('/api/sieve-analysis/meta'),
  create: (data: SieveProjectInput) => api.post<SieveProject>('/api/sieve-analysis', data),
  update: (id: number, data: SieveProjectInput) => api.put<SieveProject>(`/api/sieve-analysis/${id}`, data),
  remove: (id: number) => api.del<void>(`/api/sieve-analysis/${id}`),

  addSample: (id: number, data: SieveSampleInput) =>
    api.post<SieveProject>(`/api/sieve-analysis/${id}/samples`, data),
  updateSample: (id: number, sampleId: number, data: SieveSampleInput) =>
    api.put<SieveProject>(`/api/sieve-analysis/${id}/samples/${sampleId}`, data),
  deleteSample: (id: number, sampleId: number) =>
    api.del<SieveProject>(`/api/sieve-analysis/${id}/samples/${sampleId}`),
  updateDecision: (id: number, sampleId: number, data: SieveDecisionInput) =>
    api.put<SieveProject>(`/api/sieve-analysis/${id}/samples/${sampleId}/decision`, data),

  /** Odczyt masy frakcji + głębokości ze zdjęcia karty laboratoryjnej (OCR + AI). */
  extract: (file: File, signal?: AbortSignal, onProgress?: (p: UploadProgress) => void): Promise<SieveExtraction> => {
    const fd = new FormData();
    fd.append('file', file);
    return xhrUpload<SieveExtraction>('/api/sieve-analysis/extract', fd, { onProgress, signal });
  },

  /** Sekcje raportu — odpowiednik panelu eksportu w prototypie GranuFilt. */
  reportPdfUrl: (id: number, opt?: SieveReportOptions) => {
    const q = new URLSearchParams();
    if (opt) {
      q.set('params', String(opt.params));
      q.set('comparison', String(opt.comparison));
      q.set('decisions', String(opt.decisions));
      q.set('methodology', String(opt.methodology));
      q.set('landscape', String(opt.landscape));
    }
    const qs = q.toString();
    return `/api/sieve-analysis/${id}/report.pdf` + (qs ? `?${qs}` : '');
  },

  /** Zapisuje raport w archiwum (podpięty pod projekt). */
  archiveReport: (id: number, opt: SieveReportOptions) =>
    api.post<SieveReportArchiveResult>(
      `/api/sieve-analysis/${id}/report/archive?params=${opt.params}&comparison=${opt.comparison}`
      + `&decisions=${opt.decisions}&methodology=${opt.methodology}&landscape=${opt.landscape}`, {}),
};

// ── Ocena ryzyka (art. 4f/4g/4i) ─────────────────────────────────────────
import type {
  RiskAssessment, RiskAssessmentSummary, RiskAssessmentStatusCode, RiskLayerCode,
  RiskMeta, RiskNarrativeSection, RiskDocumentVersion, IntakeRiskReadiness,
  RiskCatchment, RiskProtectionZone, RiskEvidenceFact, RiskGap, RiskPressureOverlay, RiskChecklistItem, RiskSourceStatus,
  RiskPressureFeatureGeo, RiskGenerationStage, RiskPressureDetail, RiskAssessmentWell,
  RiskSourceDocument, RiskSourceUploadSummary,
  RiskDocChecklist, RiskDocItem, RiskDocProposal, RiskDocStatusCode,
  RiskPackageImportResult,
} from '@/types';

export const ocenaRyzyka = {
  list: (status?: RiskAssessmentStatusCode, layer?: RiskLayerCode, clientId?: number) => {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (layer) p.set('layer', layer);
    if (clientId) p.set('clientId', String(clientId));
    const q = p.toString();
    return api.get<RiskAssessmentSummary[]>('/api/risk-assessments' + (q ? `?${q}` : ''));
  },
  get: (id: number) => api.get<RiskAssessment>(`/api/risk-assessments/${id}`),
  meta: () => api.get<RiskMeta>('/api/risk-assessments/meta'),
  intakeReadiness: (intakeId: number) =>
    api.get<IntakeRiskReadiness>(`/api/risk-assessments/intake-readiness/${intakeId}`),
  create: (data: Record<string, unknown>) => api.post<RiskAssessment>('/api/risk-assessments', data),
  /** Utworzenie oceny z paczki danych wejściowych ZIP wyeksportowanej z HydroNexis (CSEU). */
  importPackage: (file: File, onProgress?: (p: UploadProgress) => void): Promise<RiskPackageImportResult> => {
    const fd = new FormData();
    fd.append('file', file);
    return xhrUpload<RiskPackageImportResult>('/api/risk-assessments/import-package', fd, { onProgress });
  },
  update: (id: number, data: Record<string, unknown>) => api.put<RiskAssessment>(`/api/risk-assessments/${id}`, data),
  remove: (id: number) => api.del<void>(`/api/risk-assessments/${id}`),

  saveInput: (id: number, dataJson: string, source?: string) =>
    api.put<RiskAssessment>(`/api/risk-assessments/${id}/input-data`, { dataJson, source }),
  generateDraft: (id: number) => api.post<RiskAssessment>(`/api/risk-assessments/${id}/generate-draft`),
  autoGenerate: (id: number) => api.post<RiskAssessment>(`/api/risk-assessments/${id}/auto-generate`),
  autofillFromDocument: (id: number, file: File, onProgress?: (p: UploadProgress) => void): Promise<RiskAssessment> => {
    const fd = new FormData();
    fd.append('file', file);
    return xhrUpload<RiskAssessment>(`/api/risk-assessments/${id}/autofill-from-document`, fd, { onProgress });
  },
  /** Ocena bez ujęcia: ręczna aktualizacja danych ujęcia (współrzędne, JCWPd, obiekt, miejscowość). */
  saveSpatial: (id: number, data: Record<string, unknown>) =>
    api.put<RiskAssessment>(`/api/risk-assessments/${id}/spatial`, data),
  /** Ocena bez ujęcia: AI-odczyt danych ujęcia (współrzędne/JCWPd/nazwa) z dokumentacji ujęcia. */
  extractIntakeDocument: (id: number, file: File, onProgress?: (p: UploadProgress) => void): Promise<RiskAssessment> => {
    const fd = new FormData();
    fd.append('file', file);
    return xhrUpload<RiskAssessment>(`/api/risk-assessments/${id}/extract-intake-document`, fd, { onProgress });
  },
  aiSuggestHazards: (id: number) => api.post<RiskAssessment>(`/api/risk-assessments/${id}/ai-suggest-hazards`),
  acceptAllHazards: (id: number) => api.post<RiskAssessment>(`/api/risk-assessments/${id}/hazards/accept-all`),

  addHazard: (id: number, d: Record<string, unknown>) => api.post<RiskAssessment>(`/api/risk-assessments/${id}/hazards`, d),
  updateHazard: (id: number, hid: number, d: Record<string, unknown>) => api.put<RiskAssessment>(`/api/risk-assessments/${id}/hazards/${hid}`, d),
  deleteHazard: (id: number, hid: number) => api.del<RiskAssessment>(`/api/risk-assessments/${id}/hazards/${hid}`),
  addControl: (id: number, hid: number, d: Record<string, unknown>) => api.post<RiskAssessment>(`/api/risk-assessments/${id}/hazards/${hid}/controls`, d),
  /** Patch częściowy środka kontroli; `clearDeadline` usuwa termin wdrożenia (sam patch tego nie odróżnia). */
  updateControl: (id: number, hid: number, cid: number, d: Record<string, unknown>, clearDeadline = false) =>
    api.put<RiskAssessment>(
      `/api/risk-assessments/${id}/hazards/${hid}/controls/${cid}` + (clearDeadline ? '?clearDeadline=true' : ''), d),
  deleteControl: (id: number, hid: number, cid: number) => api.del<RiskAssessment>(`/api/risk-assessments/${id}/hazards/${hid}/controls/${cid}`),

  addMonitoring: (id: number, d: Record<string, unknown>) => api.post<RiskAssessment>(`/api/risk-assessments/${id}/monitoring`, d),
  updateMonitoring: (id: number, mid: number, d: Record<string, unknown>) => api.put<RiskAssessment>(`/api/risk-assessments/${id}/monitoring/${mid}`, d),
  deleteMonitoring: (id: number, mid: number) => api.del<RiskAssessment>(`/api/risk-assessments/${id}/monitoring/${mid}`),

  generateSection: (id: number, sid: number) => api.post<RiskNarrativeSection>(`/api/risk-assessments/${id}/sections/${sid}/generate`),
  generateAllSections: (id: number) => api.post<RiskAssessment>(`/api/risk-assessments/${id}/sections/generate-all`),
  regenerateStaleSections: (id: number) => api.post<RiskAssessment>(`/api/risk-assessments/${id}/sections/regenerate-stale`),
  saveSection: (id: number, sid: number, editedText: string, accept: boolean) =>
    api.put<RiskNarrativeSection>(`/api/risk-assessments/${id}/sections/${sid}`, { editedText, accept }),

  submitReview: (id: number) => api.post<RiskAssessment>(`/api/risk-assessments/${id}/submit-review`),
  startReview: (id: number) => api.post<RiskAssessment>(`/api/risk-assessments/${id}/start-review`),
  requestChanges: (id: number, reason: string) => api.post<RiskAssessment>(`/api/risk-assessments/${id}/request-changes`, { reason }),
  sign: (id: number) => api.post<RiskAssessment>(`/api/risk-assessments/${id}/sign`),
  handOff: (id: number) => api.post<RiskAssessment>(`/api/risk-assessments/${id}/hand-off`),
  cancel: (id: number) => api.post<RiskAssessment>(`/api/risk-assessments/${id}/cancel`),

  addAssignment: (id: number, userId: number, role: string, note?: string) =>
    api.post<RiskAssessment>(`/api/risk-assessments/${id}/assignments`, { userId, role, note }),
  removeAssignment: (id: number, aid: number) => api.del<RiskAssessment>(`/api/risk-assessments/${id}/assignments/${aid}`),

  catchment: (id: number) => api.get<RiskCatchment | null>(`/api/risk-assessments/${id}/catchment`),
  /** F2c: zapisuje realny obszar oddziaływania (OSW) — poligon WGS84 jako string GeoJSON. */
  saveCatchment: (id: number, geoJson: string, strategy: 'IMPORTED' | 'DRAWN' = 'IMPORTED') =>
    api.put<RiskCatchment>(`/api/risk-assessments/${id}/catchment`, { geoJson, strategy }),
  /** F2c: cofa nadpisanie OSW → wraca liczony bufor Sichardta. */
  resetCatchment: (id: number) => api.del<RiskCatchment>(`/api/risk-assessments/${id}/catchment`),
  protectionZones: (id: number) => api.get<RiskProtectionZone[]>(`/api/risk-assessments/${id}/protection-zones`),
  saveProtectionZone: (id: number, geoJson: string, kind: string) =>
    api.put<RiskProtectionZone[]>(`/api/risk-assessments/${id}/protection-zone`, { geoJson, kind }),
  deleteProtectionZone: (id: number, kind?: string) =>
    api.del<RiskProtectionZone[]>(`/api/risk-assessments/${id}/protection-zone${kind ? `?kind=${kind}` : ''}`),
  evidence: (id: number) => api.get<RiskEvidenceFact[]>(`/api/risk-assessments/${id}/evidence`),
  gaps: (id: number) => api.get<RiskGap[]>(`/api/risk-assessments/${id}/gaps`),
  overlay: (id: number) => api.get<RiskPressureOverlay[]>(`/api/risk-assessments/${id}/overlay`),
  checklist: (id: number) => api.get<RiskChecklistItem[]>(`/api/risk-assessments/${id}/checklist`),
  sourceStatus: (id: number) => api.get<RiskSourceStatus[]>(`/api/risk-assessments/${id}/source-status`),
  /** „Odczytaj ponownie" jedno źródło punktowe (wymuszony świeży lookup dla ujęcia); zwraca zaktualizowany status. */
  rereadSource: (id: number, code: string) =>
    api.post<RiskSourceStatus[]>(`/api/risk-assessments/${id}/source-status/${code}/reread`),
  /** „Odczytaj wszystkie" źródła punktowe (wymuszony świeży lookup); zwraca zaktualizowany status. */
  rereadAllSources: (id: number) =>
    api.post<RiskSourceStatus[]>(`/api/risk-assessments/${id}/source-status/reread-all`),
  /** „Pobierz dla tej oceny" warstwę presji (koperta wokół współrzędnych oceny); zwraca zaktualizowany status. */
  uploadSourceLayer: (id: number, code: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return xhrUpload<RiskSourceStatus[]>(`/api/risk-assessments/${id}/source-status/${code}/upload`, fd, {});
  },
  fetchSourceLayer: (id: number, code: string) =>
    api.post<RiskSourceStatus[]>(`/api/risk-assessments/${id}/source-status/${code}/fetch`),
  /** Kurowany zestaw warstw mapy dla oceny art. 4f (kody + domyślna widoczność) — seed mapy poglądowej. */
  mapLayers: () => api.get<{ code: string; visible: boolean }[]>(`/api/risk-assessments/map-layers`),
  overlayGeoJson: (id: number) => api.get<RiskPressureFeatureGeo[]>(`/api/risk-assessments/${id}/overlay-geojson`),
  pressureDetail: (id: number, overlayId: number) =>
    api.get<RiskPressureDetail>(`/api/risk-assessments/${id}/overlay/${overlayId}`),
  wells: (id: number) => api.get<RiskAssessmentWell[]>(`/api/risk-assessments/${id}/wells`),
  addWell: (id: number, body: { designation: string; function?: string | null; latitude?: number | null; longitude?: number | null; yieldM3h?: number | null }) =>
    api.post<RiskAssessmentWell[]>(`/api/risk-assessments/${id}/wells`, body),
  updateWell: (id: number, wellId: number, body: { designation?: string; function?: string | null; latitude?: number | null; longitude?: number | null; yieldM3h?: number | null }) =>
    api.put<RiskAssessmentWell[]>(`/api/risk-assessments/${id}/wells/${wellId}`, body),
  deleteWell: (id: number, wellId: number) =>
    api.del<RiskAssessmentWell[]>(`/api/risk-assessments/${id}/wells/${wellId}`),
  saveCatchmentMap: (id: number, file: File) => { const fd = new FormData(); fd.append('file', file); return xhrUpload<{ filename: string; contentType: string; sizeBytes: number }>(`/api/risk-assessments/${id}/catchment/source-map`, fd); },
  catchmentMapInfo: (id: number) => api.get<{ filename: string; contentType: string; sizeBytes: number } | null>(`/api/risk-assessments/${id}/catchment/source-map/info`),
  catchmentMapUrl: (id: number) => `/api/risk-assessments/${id}/catchment/source-map`,
  deleteCatchmentMap: (id: number) => api.del<void>(`/api/risk-assessments/${id}/catchment/source-map`),
  proposeOutline: (id: number, file: File) => { const fd = new FormData(); fd.append('file', file); return xhrUpload<{ points: number[][] }>(`/api/risk-assessments/${id}/catchment/propose-outline`, fd); },
  stages: (id: number) => api.get<RiskGenerationStage[]>(`/api/risk-assessments/${id}/stages`),
  /**
   * Auto-generacja ze strumieniem etapów (F3). onStage otrzymuje każdą zmianę statusu etapu na żywo
   * (PENDING→RUNNING→OK/FAILED); zwraca finalne DTO oceny z eventu „done". Wzorzec jak mapy.fetchWithProgress.
   */
  autoGenerateStream: async (id: number, onStage: (s: RiskGenerationStage) => void,
                             fromStage?: string): Promise<RiskAssessment> => {
    const qs = fromStage ? `?fromStage=${encodeURIComponent(fromStage)}` : '';
    return strumienZWynikiem<RiskAssessment>(`/api/risk-assessments/${id}/auto-generate/stream${qs}`, {
      stage: json<RiskGenerationStage>(onStage),
    }, { kontekst: 'Auto-generacja oceny' });
  },
  resolveGap: (id: number, gapId: number, resolved: boolean) =>
    api.post<RiskAssessment>(`/api/risk-assessments/${id}/gaps/${gapId}/resolve`, { resolved }),
  versions: (id: number) => api.get<RiskDocumentVersion[]>(`/api/risk-assessments/${id}/versions`),
  pdfUrl: (id: number) => `/api/risk-assessments/${id}/pdf`,
  /** Paczka wynikowa (manifest + podpisany PDF) do zaimportowania w HydroNexis/CSEU. */
  resultPackageUrl: (id: number) => `/api/risk-assessments/${id}/result-package`,
  exportGeoJsonUrl: (id: number) => `/api/risk-assessments/${id}/export/geojson`,
  downloadUrl: (id: number, docId: number) => `/api/risk-assessments/${id}/documents/${docId}/download`,
  // ── dokumenty źródłowe (drag&drop + folder, indeksowane przez AI) ──
  sourceDocuments: (id: number) => api.get<RiskSourceDocument[]>(`/api/risk-assessments/${id}/source-documents`),
  /** Odczyt danych wejściowych z wgranego już dokumentu źródłowego (uzupełnia tylko puste pola). */
  autofillFromSourceDocument: (id: number, docId: number) =>
    api.post<RiskAssessment>(`/api/risk-assessments/${id}/source-documents/${docId}/autofill`),
  uploadSourceDocuments: (id: number, files: File[], onProgress?: (p: UploadProgress) => void): Promise<RiskSourceUploadSummary> => {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    return xhrUpload<RiskSourceUploadSummary>(`/api/risk-assessments/${id}/source-documents`, fd, { onProgress });
  },
  sourceDocumentDownloadUrl: (id: number, docId: number) => `/api/risk-assessments/${id}/source-documents/${docId}/download`,
  deleteSourceDocument: (id: number, docId: number) => api.del<void>(`/api/risk-assessments/${id}/source-documents/${docId}`),
  // ── Etap 2: checklista wymaganych dokumentów + propozycje AI ──
  requiredDocuments: (id: number) => api.get<RiskDocChecklist>(`/api/risk-assessments/${id}/required-documents`),
  changeReqDocStatus: (id: number, rid: number, status: RiskDocStatusCode, note?: string) =>
    api.put<RiskDocItem>(`/api/risk-assessments/${id}/required-documents/${rid}/status`, { status, note }),
  refreshReqDocs: (id: number) => api.post<{ added: number }>(`/api/risk-assessments/${id}/required-documents/refresh`),
  linkReqDocFile: (id: number, rid: number, docId: number) =>
    api.post<RiskDocItem>(`/api/risk-assessments/${id}/required-documents/${rid}/files/${docId}/link`),
  unlinkReqDocFile: (id: number, rid: number, docId: number) =>
    api.del<RiskDocItem>(`/api/risk-assessments/${id}/required-documents/${rid}/files/${docId}`),
  classifyDocuments: (id: number) => api.post<RiskDocProposal[]>(`/api/risk-assessments/${id}/document-proposals/classify`),
  documentProposals: (id: number) => api.get<RiskDocProposal[]>(`/api/risk-assessments/${id}/document-proposals`),
  acceptProposal: (id: number, pid: number, overrideItemId?: number) =>
    api.post<RiskDocProposal>(`/api/risk-assessments/${id}/document-proposals/${pid}/accept`, { overrideItemId }),
  rejectProposal: (id: number, pid: number) =>
    api.post<RiskDocProposal>(`/api/risk-assessments/${id}/document-proposals/${pid}/reject`),
};

// ===== Analiza ryzyka (art. 133) — dane strukturalne opracowania typu ANALIZA_RYZYKA_6 =====
export interface AnalizaField { key: string; label: string; kind: string; group: string; required: boolean; unit: string | null; dictType: string | null; }
export interface AnalizaDictEntry { code: string | null; label: string; value: number | null; }
export interface AnalizaRiskClassDef { name: string; label: string; hex: string; min: number; max: number; }
export interface AnalizaMeta { fields: AnalizaField[]; dictionaries: Record<string, AnalizaDictEntry[]>; riskClasses: AnalizaRiskClassDef[]; }
export interface AnalizaInputData { id: number | null; version: number; data: Record<string, unknown>; complete: boolean; missingFields: string[]; source: string; }
export interface AnalizaHazard { id: number; art130Point: string | null; category: string | null; title: string; description: string | null; p: number; c: number; v: number; r: number; riskClass: string; riskClassLabel: string; riskClassHex: string; uncertainty: boolean; aiGenerated: boolean; sortOrder: number; }
export interface AnalizaCalcResult { id: number; method: string; inputs: Record<string, unknown>; outputs: Record<string, unknown>; summary: string | null; createdAt: string | null; }
export interface AnalizaMatrixSlice { c: number; grid: number[][]; }
export interface AnalizaMatrix { hazards: AnalizaHazard[]; distribution: Record<string, number>; slices: AnalizaMatrixSlice[]; worstClass: string | null; }
export interface AnalizaCalcOutcome { method: string; ok: boolean; summary: string | null; error: string | null; }
export interface AnalizaCalcRun { outcomes: AnalizaCalcOutcome[]; }
export interface AnalizaExtractResult { fields: Record<string, unknown>; extractedFields: string[]; sourceTextPreview: string; provider: string; model: string; mock: boolean; }

const AR = (id: number) => `/api/studies/${id}/risk-analysis`;

export const riskAnalysis = {
  meta: (id: number) => api.get<AnalizaMeta>(`${AR(id)}/meta`),
  getInput: (id: number) => api.get<AnalizaInputData>(`${AR(id)}/input-data`),
  saveInput: (id: number, data: Record<string, unknown>, source?: string) =>
    api.put<AnalizaInputData>(`${AR(id)}/input-data`, { data, source }),
  calc: (id: number) => api.post<AnalizaCalcRun>(`${AR(id)}/calc`),
  getCalc: (id: number) => api.get<AnalizaCalcResult[]>(`${AR(id)}/calc`),
  hazards: (id: number) => api.get<AnalizaHazard[]>(`${AR(id)}/hazards`),
  addHazard: (id: number, d: Record<string, unknown>) => api.post<AnalizaHazard>(`${AR(id)}/hazards`, d),
  updateHazard: (id: number, hid: number, d: Record<string, unknown>) => api.put<AnalizaHazard>(`${AR(id)}/hazards/${hid}`, d),
  deleteHazard: (id: number, hid: number) => api.del<void>(`${AR(id)}/hazards/${hid}`),
  seedHazards: (id: number) => api.post<AnalizaHazard[]>(`${AR(id)}/hazards/seed`),
  aiSuggest: (id: number) => api.post<AnalizaHazard[]>(`${AR(id)}/hazards/ai-suggest`),
  matrix: (id: number) => api.get<AnalizaMatrix>(`${AR(id)}/matrix`),
  completeness: (id: number) => api.get<CompletenessReport>(`${AR(id)}/completeness`),
  extract: (id: number, file: File, onProgress?: (p: UploadProgress) => void): Promise<AnalizaExtractResult> => {
    const fd = new FormData();
    fd.append('file', file);
    return xhrUpload<AnalizaExtractResult>(`${AR(id)}/extract`, fd, { onProgress });
  },
};

export interface AnalizaFleetRow {
  studyId: number; number: string; title: string; intakeName: string | null; town: string | null;
  status: string; statusColor: string; inputComplete: boolean; calcDone: boolean;
  requiresPosrednia: boolean | null; hazardCount: number; worstClass: string | null;
  conclusionAccepted: boolean; docsReceived: number; docsTotal: number;
}

export const analizaFleet = {
  list: (clientId?: number) =>
    api.get<AnalizaFleetRow[]>('/api/analiza-ryzyka/fleet' + (clientId ? `?clientId=${clientId}` : '')),
};

// ─────────────────────────────────────────────────────────────────────────────
// Asystent AI — czat nad korpusem wiedzy + zarządzanie korpusami
// ─────────────────────────────────────────────────────────────────────────────

export interface AsystentKorpus {
  id: number;
  code: string;
  name: string;
  description: string | null;
  sampleQuestions: string | null;
  sourceCount: number;
  fragmentCount: number;
}

export interface AsystentRozmowa {
  id: number;
  corpusId: number;
  corpusName: string;
  title: string | null;
  pinned: boolean;
  lastMessageAt: string | null;
}

export interface AsystentCytat {
  id: number;
  marker: string;
  fragmentId: number | null;
  label: string;
  snippet: string | null;
  targetRoute: string | null;
  vecScore: number | null;
}

export interface AsystentWiadomosc {
  id: number;
  role: 'USER' | 'ASSISTANT';
  content: string;
  noAnswer: boolean;
  model: string | null;
  provider: string | null;
  error: string | null;
  createdAt: string;
  cytowania: AsystentCytat[];
}

export const asystent = {
  korpusy: () => api.get<AsystentKorpus[]>('/api/asystent/korpusy'),
  rozmowy: () => api.get<AsystentRozmowa[]>('/api/asystent/rozmowy'),
  rozpocznij: (corpusId: number) =>
    api.post<AsystentRozmowa>(`/api/asystent/rozmowy?corpusId=${corpusId}`, {}),
  wiadomosci: (id: number) => api.get<AsystentWiadomosc[]>(`/api/asystent/rozmowy/${id}/wiadomosci`),
  zapytaj: (id: number, pytanie: string) =>
    api.post<AsystentWiadomosc>(`/api/asystent/rozmowy/${id}/pytanie`, { pytanie }),
  /**
   * Strumieniowa odmiana: `onSources` dostaje materiały jeszcze przed pierwszym tokenem,
   * `onDelta` każdy przyrost tekstu, a zwrócona obietnica — zapisaną wiadomość z cytatami.
   */
  zapytajStrumieniowo: (
    id: number,
    pytanie: string,
    onDelta: (fragment: string) => void,
    onSources: (c: AsystentCytat[]) => void,
    signal?: AbortSignal,
  ) =>
    strumienZWynikiem<AsystentWiadomosc>(`/api/asystent/rozmowy/${id}/pytanie/stream`, {
      sources: json<AsystentCytat[]>(onSources),
      // Delty idą JSON-em ({t}), nie czystym tekstem: kodowanie SSE nie odróżnia spacji
      // należącej do ładunku od separatora pola, a modele strumieniują tokeny z wiodącą spacją.
      delta: json<{ t: string }>(v => onDelta(v.t)),
    }, { body: { pytanie }, kontekst: 'Pytanie do asystenta', signal }),
  przypnij: (id: number, pinned: boolean) =>
    api.put<AsystentRozmowa>(`/api/asystent/rozmowy/${id}/przypnij?pinned=${pinned}`, {}),
  usun: (id: number) => api.del<void>(`/api/asystent/rozmowy/${id}`),
};

export interface KorpusSzczegoly extends AsystentKorpus {
  systemPrompt: string | null;
  topK: number;
  minScore: number;
  active: boolean;
  staleCount: number;
  lastIndexedAt: string | null;
}

export interface KorpusPozycja {
  id: number;
  rodzaj: 'PLIK' | 'WIEDZA';
  documentId: number | null;
  sourceId: number | null;
  nazwa: string;
  included: boolean;
  origin: 'MANUAL' | 'RULE';
  fragmenty: number | null;
  nieaktualne: boolean;
}

export interface KorpusRegula {
  id: number;
  ruleType: string;
  opis: string;
  paramsJson: string;
  includeSensitive: boolean;
  active: boolean;
  lastAppliedAt: string | null;
  lastMatchedCount: number | null;
}

export interface RegulaPodglad {
  dopasowane: number;
  pominieteWrazliwe: number;
  documentIds: number[];
  sourceIds: number[];
}

export interface RodzajWiedzy {
  kod: string;
  opis: string;
  zrodel: number;
  fragmentow: number;
  nieaktualnych: number;
}

export interface KorpusGrantUzytkownika {
  userId: number;
  login: string;
  imieNazwisko: string;
}

/** Pozycja wybieraka plików. `wrazliwy` = dokument kadr, faktur albo floty. */
export interface KorpusDokumentDoWyboru {
  id: number;
  nazwa: string;
  typOpracowania: string | null;
  kategoria: string | null;
  wrazliwy: boolean;
}

export interface KorpusZrodloDoWyboru {
  id: number;
  kod: string;
  rodzaj: string;
  tytul: string;
  fragmenty: number | null;
}

export const korpusy = {
  lista: () => api.get<KorpusSzczegoly[]>('/api/admin/korpusy'),
  jeden: (id: number) => api.get<KorpusSzczegoly>(`/api/admin/korpusy/${id}`),
  utworz: (body: Partial<KorpusSzczegoly>) =>
    api.post<KorpusSzczegoly>('/api/admin/korpusy', body),
  zapisz: (id: number, body: Partial<KorpusSzczegoly>) =>
    api.put<KorpusSzczegoly>(`/api/admin/korpusy/${id}`, body),
  /** `zRozmowami` = zgoda na skasowanie rozmów opartych na korpusie; serwer bez niej odmawia. */
  usunKorpus: (id: number, zRozmowami = false) =>
    api.del<void>(`/api/admin/korpusy/${id}?zRozmowami=${zRozmowami}`),
  sklad: (id: number) => api.get<KorpusPozycja[]>(`/api/admin/korpusy/${id}/sklad`),
  przelacz: (id: number, memberId: number, included: boolean) =>
    api.put<void>(`/api/admin/korpusy/${id}/sklad/${memberId}?included=${included}`, {}),
  usunPozycje: (id: number, memberId: number) =>
    api.del<void>(`/api/admin/korpusy/${id}/sklad/${memberId}`),
  dodajZrodlo: (id: number, sourceId: number) =>
    api.post<KorpusPozycja>(`/api/admin/korpusy/${id}/sklad/zrodlo?sourceId=${sourceId}`, {}),
  dodajDokument: (id: number, documentId: number) =>
    api.post<KorpusPozycja>(`/api/admin/korpusy/${id}/sklad/dokument?documentId=${documentId}`, {}),
  /** Wybierak plików. `wrazliwe` domyślnie false — kadry/faktury/flota tylko na żądanie. */
  dokumentyDoWyboru: (szukaj: string, wrazliwe = false) =>
    api.get<KorpusDokumentDoWyboru[]>(
      `/api/admin/korpusy/dokumenty?wrazliwe=${wrazliwe}`
      + (szukaj ? `&szukaj=${encodeURIComponent(szukaj)}` : ''),
    ),
  zrodlaDoWyboru: (szukaj: string) =>
    api.get<KorpusZrodloDoWyboru[]>(
      '/api/admin/korpusy/zrodla' + (szukaj ? `?szukaj=${encodeURIComponent(szukaj)}` : ''),
    ),
  reguly: (id: number) => api.get<KorpusRegula[]>(`/api/admin/korpusy/${id}/reguly`),
  zapiszRegule: (id: number, regula: Partial<KorpusRegula>) =>
    api.post<KorpusRegula>(`/api/admin/korpusy/${id}/reguly`, regula),
  usunRegule: (id: number, ruleId: number) =>
    api.del<number>(`/api/admin/korpusy/${id}/reguly/${ruleId}`),
  podglad: (id: number, regula: Partial<KorpusRegula>) =>
    api.post<RegulaPodglad>(`/api/admin/korpusy/${id}/reguly/podglad`, regula),
  zastosuj: (id: number, ruleId: number) =>
    api.post<RegulaPodglad>(`/api/admin/korpusy/${id}/reguly/${ruleId}/zastosuj`, {}),
  dostep: (id: number) => api.get<string[]>(`/api/admin/korpusy/${id}/dostep`),
  dostepUzytkownikow: (id: number) =>
    api.get<KorpusGrantUzytkownika[]>(`/api/admin/korpusy/${id}/dostep/uzytkownicy`),
  nadajUzytkownikowi: (id: number, userId: number) =>
    api.post<void>(`/api/admin/korpusy/${id}/dostep/uzytkownicy?userId=${userId}`, {}),
  odbierzUzytkownikowi: (id: number, userId: number) =>
    api.del<void>(`/api/admin/korpusy/${id}/dostep/uzytkownicy?userId=${userId}`),
  nadaj: (id: number, role: string) =>
    api.post<void>(`/api/admin/korpusy/${id}/dostep?role=${encodeURIComponent(role)}`, {}),
  odbierz: (id: number, role: string) =>
    api.del<void>(`/api/admin/korpusy/${id}/dostep?role=${encodeURIComponent(role)}`),
  rodzajeWiedzy: () => api.get<RodzajWiedzy[]>('/api/admin/korpusy/wiedza/rodzaje'),
  odswiezWiedze: (kind?: string, forceAll = false) =>
    api.post<number>(
      `/api/admin/korpusy/wiedza/odswiez?forceAll=${forceAll}` + (kind ? `&kind=${kind}` : ''),
      {},
    ),
  zapiszNotatke: (id: number | null, title: string, tekst: string) =>
    api.post<number>(
      '/api/admin/korpusy/wiedza/notatki' + (id ? `?id=${id}` : ''),
      { title, tekst },
    ),
  usunNotatke: (id: number) => api.del<void>(`/api/admin/korpusy/wiedza/notatki/${id}`),
};

// =============================================================================
// Prace terenowe — ewidencja prac (V154)
// =============================================================================

export type FieldWorkStatusCode = 'OCZEKIWANE' | 'ZLECONE' | 'W_TOKU' | 'ZAKONCZONE';

/** Kod rodzaju z cennika prac terenowych (V186) — słownik w bazie, nie stała lista. */
export type FieldWorkKindCode = string;

/** Rodzaj pracy terenowej z ceną (V186). {@code unitPriceNet} null bez PRICING_VIEW albo gdy rodzaj nie ma ceny. */
export interface FieldWorkKindEntry {
  id: number;
  code: string;
  name: string;
  unit: string;
  unitPriceNet: number | null;
  cyclic: boolean;
  description: string | null;
  sortOrder: number;
  active: boolean;
}

export interface FieldWorkKindInput {
  name?: string;
  unit?: string;
  unitPriceNet?: number | null;
  /** true usuwa cenę (sam null w PUT znaczy „bez zmian”). */
  clearPrice?: boolean;
  cyclic?: boolean;
  description?: string | null;
  sortOrder?: number;
  active?: boolean;
}

export const fieldWorkKinds = {
  list: (includeInactive?: boolean) =>
    api.get<FieldWorkKindEntry[]>('/api/field-work-kinds' + (includeInactive ? '?includeInactive=true' : '')),
  create: (data: FieldWorkKindInput) => api.post<FieldWorkKindEntry>('/api/field-work-kinds', data),
  update: (id: number, data: FieldWorkKindInput) => api.put<FieldWorkKindEntry>(`/api/field-work-kinds/${id}`, data),
  delete: (id: number) => api.del<{ ok: boolean; deactivated: boolean }>(`/api/field-work-kinds/${id}`),
};

export interface FieldWorkDate {
  id: number | null;
  seq: number;
  dateFrom: string | null;
  dateTo: string | null;
  /** Zapis, którego importu nie dało się rozłożyć na daty. Tylko gdy dateFrom === null. */
  rawText: string | null;
  note: string | null;
  dayCount: number;
}

export interface FieldWorkInvoiceLink {
  id: number;
  invoiceId: number;
  invoiceNumber: string | null;
  ksefNumber: string | null;
  issueDate: string | null;
  totalNet: number | null;
  totalGross: number | null;
  paymentStatus: string;
  paymentStatusName: string;
  paymentStatusColor: string;
  paidDate: string | null;
  netAmount: number | null;
  note: string | null;
}

export interface FieldWork {
  id: number;
  seqNo: number;
  clientId: number | null;
  clientName: string | null;
  clientCity: string | null;
  title: string;
  description: string | null;
  kind: FieldWorkKindCode;
  kindName: string;
  status: FieldWorkStatusCode;
  statusName: string;
  statusColor: string;
  wellLabel: string | null;
  locationText: string | null;
  parcel: string | null;
  latitude: number | null;
  longitude: number | null;
  mapLatitude: number | null;
  mapLongitude: number | null;
  inheritedLocation: boolean;
  contractDeadline: string | null;
  deadlineNote: string | null;
  plannedDate: string | null;
  /** Ujemne = po terminie. null dla prac zakończonych albo bez terminu. */
  daysToDeadline: number | null;
  /** null gdy użytkownik nie ma PRICING_VIEW — redakcja jest po stronie serwera. */
  netAmount: number | null;
  currency: string;
  invoicedDate: string | null;
  settled: boolean;
  invoiceCount: number;
  /** null gdy użytkownik nie ma FINANCE_VIEW. */
  invoices: FieldWorkInvoiceLink[] | null;
  dates: FieldWorkDate[];
  firstDate: string | null;
  lastDate: string | null;
  totalDays: number;
  /** Zlecenie. null bez uprawnienia FIELDWORK_ORDER_EDIT (redakcja serwerowa). */
  orderId: number | null;
  orderNumber: string | null;
  /** Oferta źródłowa. null bez OFFER_VIEW. */
  offerId: number | null;
  offerNumber: string | null;
  intakeId: number | null;
  intakeName: string | null;
  studyId: number | null;
  studyNumber: string | null;
  contractId: number | null;
  contractNumber: string | null;
  managerId: number | null;
  managerName: string | null;
  notes: string | null;
}

export interface FieldWorkInput {
  clientId: number | null;
  title: string;
  description?: string | null;
  kind?: FieldWorkKindCode;
  status?: FieldWorkStatusCode;
  wellLabel?: string | null;
  locationText?: string | null;
  parcel?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  contractDeadline?: string | null;
  deadlineNote?: string | null;
  plannedDate?: string | null;
  netAmount?: number | null;
  invoicedDate?: string | null;
  notes?: string | null;
  intakeId?: number | null;
  studyId?: number | null;
  contractId?: number | null;
  managerId?: number | null;
  orderId?: number | null;
  offerId?: number | null;
  dates?: { dateFrom?: string | null; dateTo?: string | null; rawText?: string | null; note?: string | null }[];
}

export interface FieldWorkSummary {
  total: number;
  ordered: number;
  inProgress: number;
  pending: number;
  done: number;
  overdue: number;
  dueIn7Days: number;
  unsettled: number;
  /** null bez PRICING_VIEW. */
  netTotal: number | null;
  netUnsettled: number | null;
}

export interface FieldWorkMapPoint {
  clientId: number;
  clientName: string;
  city: string | null;
  latitude: number;
  longitude: number;
  locationNote: string | null;
  dominantStatus: FieldWorkStatusCode | null;
  dominantStatusName: string;
  /** HEX policzony na serwerze — legenda i pinezki mają jedno źródło prawdy. */
  color: string;
  worksTotal: number;
  worksInProgress: number;
  worksOrdered: number;
  worksPending: number;
  worksDone: number;
  worksOverdue: number;
}

export interface FieldWorkMeta {
  statuses: { id: FieldWorkStatusCode; name: string; color: string; mapColor: string; open: boolean }[];
  kinds: { id: FieldWorkKindCode; name: string }[];
  noWorksColor: string;
}

/** Kryteria rejestru. Puste pola są pomijane — lista, podsumowanie i mapa dostają ten sam zestaw. */
export interface FieldWorkFilter {
  search?: string;
  status?: FieldWorkStatusCode[];
  kind?: FieldWorkKindCode[];
  clientId?: number | null;
  /** Zawęża rejestr do prac jednego zlecenia. */
  orderId?: number | null;
  settled?: boolean | null;
  overdue?: boolean | null;
  deadlineFrom?: string | null;
  deadlineTo?: string | null;
  executedFrom?: string | null;
  executedTo?: string | null;
  amountFrom?: number | null;
  amountTo?: number | null;
  voivodeship?: string | null;
}

function fieldWorkQuery(f?: FieldWorkFilter): string {
  const q = new URLSearchParams();
  if (!f) return '';
  const put = (k: string, v: unknown) => {
    if (v === undefined || v === null || v === '') return;
    q.append(k, String(v));
  };
  put('search', f.search?.trim());
  // Listy idą jako powtórzone parametry (?status=A&status=B) — tak Spring wiąże List<Enum>.
  f.status?.forEach(s => q.append('status', s));
  f.kind?.forEach(k => q.append('kind', k));
  put('clientId', f.clientId);
  put('orderId', f.orderId);
  put('settled', f.settled);
  put('overdue', f.overdue);
  put('deadlineFrom', f.deadlineFrom);
  put('deadlineTo', f.deadlineTo);
  put('executedFrom', f.executedFrom);
  put('executedTo', f.executedTo);
  put('amountFrom', f.amountFrom);
  put('amountTo', f.amountTo);
  put('voivodeship', f.voivodeship);
  const qs = q.toString();
  return qs ? `?${qs}` : '';
}

export const fieldWorks = {
  list: (f?: FieldWorkFilter) => api.get<FieldWork[]>('/api/field-works' + fieldWorkQuery(f)),
  summary: (f?: FieldWorkFilter) =>
    api.get<FieldWorkSummary>('/api/field-works/summary' + fieldWorkQuery(f)),
  map: (f?: FieldWorkFilter) =>
    api.get<FieldWorkMapPoint[]>('/api/field-works/map' + fieldWorkQuery(f)),
  meta: () => api.get<FieldWorkMeta>('/api/field-works/meta'),
  get: (id: number) => api.get<FieldWork>(`/api/field-works/${id}`),
  create: (data: FieldWorkInput) => api.post<FieldWork>('/api/field-works', data),
  update: (id: number, data: FieldWorkInput) => api.put<FieldWork>(`/api/field-works/${id}`, data),
  changeStatus: (id: number, status: FieldWorkStatusCode) =>
    api.post<FieldWork>(`/api/field-works/${id}/status`, { status }),
  remove: (id: number) => api.del<void>(`/api/field-works/${id}`),

  /** Faktury sprzedażowe tego klienta, jeszcze nieprzypięte. System nic nie przypina sam. */
  invoiceCandidates: (id: number) =>
    api.get<FieldWorkInvoiceLink[]>(`/api/field-works/${id}/invoice-candidates`),
  attachInvoice: (id: number, invoiceId: number, netAmount?: number | null, note?: string | null) =>
    api.post<FieldWorkInvoiceLink>(`/api/field-works/${id}/invoices`, { invoiceId, netAmount, note }),
  detachInvoice: (linkId: number) => api.del<void>(`/api/field-works/invoice-links/${linkId}`),

  /** Prace rozliczone daną fakturą — „za co to było" na karcie faktury. */
  byInvoice: (invoiceId: number) => api.get<FieldWork[]>(`/api/field-works/by-invoice/${invoiceId}`),
};

// =============================================================================
// Zlecenia na roboty (V156)
// =============================================================================

export type OrderChannelCode = 'PHONE' | 'EMAIL' | 'LETTER' | 'OFFER' | 'OTHER';

export interface WorkOrder {
  id: number;
  number: string;
  clientOrderRef: string | null;
  clientId: number | null;
  clientName: string | null;
  clientCity: string | null;
  orderDate: string;
  receivedVia: OrderChannelCode;
  receivedViaName: string;
  receivedById: number | null;
  receivedByName: string | null;
  sourceOfferId: number | null;
  sourceOfferNumber: string | null;
  /** Numer oferty wystawionej poza portalem (V174). null bez OFFER_VIEW. */
  sourceOfferRef: string | null;
  title: string;
  description: string | null;
  deadline: string | null;
  daysToDeadline: number | null;
  /** null bez PRICING_VIEW — redakcja jest po stronie serwera. Przy wariantach: suma rozliczanych. */
  netAmount: number | null;
  /** Cena w wariantach/etapach (V174); [] = jedna kwota. null bez PRICING_VIEW. */
  priceVariants: WorkOrderPriceVariant[] | null;
  currency: string;
  notes: string | null;
  cancelled: boolean;
  cancelledAt: string | null;
  cancelReason: string | null;
  worksTotal: number;
  worksInProgress: number;
  worksOrdered: number;
  worksPending: number;
  worksDone: number;
  worksOverdue: number;
  /** null = zlecenie nie ma jeszcze żadnej pracy (to alert, nie luka). */
  derivedStatus: FieldWorkStatusCode | null;
  derivedStatusName: string;
  statusColor: string;
  attachmentCount: number;
}

/** Wariant/etap ceny zlecenia. Wartość zlecenia = suma wariantów z `billed`. */
export interface WorkOrderPriceVariant {
  label: string;
  netAmount: number;
  billed: boolean;
}

export interface WorkOrderInput {
  clientId: number | null;
  title: string;
  orderDate?: string | null;
  receivedVia?: OrderChannelCode;
  clientOrderRef?: string | null;
  description?: string | null;
  deadline?: string | null;
  /** Ignorowane przez serwer, gdy zlecenie ma warianty — wtedy liczy sumę rozliczanych. */
  netAmount?: number | null;
  /** Brak pola = bez zmian, [] = cena jedną kwotą. */
  priceVariants?: WorkOrderPriceVariant[];
  notes?: string | null;
  sourceOfferId?: number | null;
  /** Numer oferty spoza portalu; serwer go pomija, gdy podpięto ofertę z portalu. */
  sourceOfferRef?: string | null;
  receivedById?: number | null;
  /**
   * Prace terenowe zakładane razem ze zleceniem — tylko przy tworzeniu. Klienta i zlecenie
   * narzuca serwer; błąd w którejkolwiek pracy cofa całe zlecenie.
   */
  works?: Omit<FieldWorkInput, 'clientId' | 'orderId'>[];
}

export interface WorkOrderSummary {
  total: number;
  active: number;
  cancelled: number;
  /** Zlecenia przyjęte, do których nikt nie rozpisał żadnej pracy. */
  withoutWorks: number;
  overdue: number;
  netTotal: number | null;
}

export interface WorkOrderMeta {
  channels: { id: OrderChannelCode; name: string; expectsDocument: boolean }[];
  noWorksColor: string;
  cancelledColor: string;
}

export interface WorkOrderFile {
  id: number;
  documentId: number;
  filename: string;
  description: string | null;
  sizeBytes: number | null;
}

export interface WorkOrderFilter {
  search?: string;
  clientId?: number | null;
  cancelled?: boolean | null;
  withoutWorks?: boolean | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

function workOrderQuery(f?: WorkOrderFilter): string {
  const q = new URLSearchParams();
  if (!f) return '';
  const put = (k: string, v: unknown) => {
    if (v === undefined || v === null || v === '') return;
    q.append(k, String(v));
  };
  put('search', f.search?.trim());
  put('clientId', f.clientId);
  put('cancelled', f.cancelled);
  put('withoutWorks', f.withoutWorks);
  put('dateFrom', f.dateFrom);
  put('dateTo', f.dateTo);
  const qs = q.toString();
  return qs ? `?${qs}` : '';
}

// ── Oś czasu zlecenia na roboty (V185) ───────────────────────────────────────────────

export type StageStatus = 'TODO' | 'IN_PROGRESS' | 'DONE' | 'SKIPPED';

export interface WorkOrderTimelineMeta {
  stages: { code: string; label: string; hints: string[] }[];
  statuses: { code: StageStatus; label: string }[];
}

export interface WorkOrderStageView {
  code: string;
  label: string;
  status: StageStatus;
  statusName: string;
  /** true = stan wyliczony z danych, false = ustawiony ręcznie */
  auto: boolean;
  date: string | null;
  note: string | null;
  updatedByName: string | null;
}

export interface WorkOrderEventView {
  /** null = wpis wyliczony (zlecenie, terminy prac, faktury) — bez edycji */
  id: number | null;
  stage: string;
  stageName: string;
  date: string;
  title: string;
  note: string | null;
  workId: number | null;
  workLabel: string | null;
  trackingNumber: string | null;
  remindOn: string | null;
  reminderSent: boolean;
  auto: boolean;
  createdByName: string | null;
}

export interface WorkOrderTimeline {
  stages: WorkOrderStageView[];
  currentStage: string;
  deadline: string | null;
  events: WorkOrderEventView[];
  warnings: { level: 'danger' | 'warn' | 'info'; text: string }[];
  works: { id: number; label: string; kindName: string; statusName: string; statusColor: string }[];
}

export interface WorkOrderEventInput {
  stage: string;
  date: string | null;
  title: string;
  note: string | null;
  workId: number | null;
  trackingNumber: string | null;
  remindOn: string | null;
}

export const workOrderTimeline = {
  meta: () => api.get<WorkOrderTimelineMeta>('/api/work-orders/timeline-meta'),
  get: (id: number) => api.get<WorkOrderTimeline>(`/api/work-orders/${id}/timeline`),
  setStage: (id: number, stage: string, data: { status: StageStatus; date: string | null; note: string | null }) =>
    api.put<WorkOrderTimeline>(`/api/work-orders/${id}/timeline/stages/${stage}`, data),
  clearStage: (id: number, stage: string) =>
    api.del<WorkOrderTimeline>(`/api/work-orders/${id}/timeline/stages/${stage}`),
  addEvent: (id: number, data: WorkOrderEventInput) =>
    api.post<WorkOrderTimeline>(`/api/work-orders/${id}/timeline/events`, data),
  updateEvent: (id: number, eventId: number, data: WorkOrderEventInput) =>
    api.put<WorkOrderTimeline>(`/api/work-orders/${id}/timeline/events/${eventId}`, data),
  deleteEvent: (id: number, eventId: number) =>
    api.del<WorkOrderTimeline>(`/api/work-orders/${id}/timeline/events/${eventId}`),
};

export const workOrders = {
  list: (f?: WorkOrderFilter) => api.get<WorkOrder[]>('/api/work-orders' + workOrderQuery(f)),
  summary: (f?: WorkOrderFilter) =>
    api.get<WorkOrderSummary>('/api/work-orders/summary' + workOrderQuery(f)),
  meta: () => api.get<WorkOrderMeta>('/api/work-orders/meta'),
  get: (id: number) => api.get<WorkOrder>(`/api/work-orders/${id}`),
  /** Zlecenia danego klienta — podpowiedzi przy wiązaniu pracy ze zleceniem. */
  byClient: (clientId: number) => api.get<WorkOrder[]>(`/api/work-orders/by-client/${clientId}`),
  works: (id: number) => api.get<FieldWork[]>(`/api/work-orders/${id}/works`),
  create: (data: WorkOrderInput) => api.post<WorkOrder>('/api/work-orders', data),
  update: (id: number, data: WorkOrderInput) => api.put<WorkOrder>(`/api/work-orders/${id}`, data),
  cancel: (id: number, reason?: string | null) =>
    api.post<WorkOrder>(`/api/work-orders/${id}/cancel`, { reason: reason ?? null }),
  restore: (id: number) => api.post<WorkOrder>(`/api/work-orders/${id}/restore`),
  remove: (id: number) => api.del<void>(`/api/work-orders/${id}`),

  files: (id: number) => api.get<WorkOrderFile[]>(`/api/work-orders/${id}/files`),
  removeFile: (attachmentId: number) => api.del<void>(`/api/work-orders/files/${attachmentId}`),
};

/** Chuda lista umów do wyboru — pełna lista ciągnie treść każdej umowy. */
export interface ContractPickerItem {
  id: number;
  number: string;
  signedDate: string | null;
  signed: boolean;
}

export const contractPicker = {
  search: (q?: string, limit = 50) => {
    const p = new URLSearchParams();
    if (q && q.trim()) p.set('q', q.trim());
    p.set('limit', String(limit));
    return api.get<ContractPickerItem[]>(`/api/contracts/picker?${p.toString()}`);
  },
};

// ==================== Dopasowanie map (georeferencja rastrów) ====================

/** Punkt kontrolny dopasowania: piksel rastra ↔ położenie w terenie. */
export interface GeorefGcp {
  id: number | null;
  px: number;
  py: number;
  /** GRID = miarodajne easting/northing w układzie mapy; WGS84 = miarodajne lat/lon. */
  groundKind: 'GRID' | 'WGS84';
  easting: number | null;
  northing: number | null;
  lat: number | null;
  lon: number | null;
  label: string | null;
  source: 'OCR_GRID' | 'AI_OBJECT' | 'BASEMAP' | 'MANUAL';
  sourceLabel?: string;
  active: boolean;
  /** Odległość między pomiarem a dopasowaniem [m] — im większa, tym pilniejsza poprawka. */
  residualM: number | null;
}

export interface GeorefMap {
  id: number;
  name: string;
  note: string | null;
  status: 'DRAFT' | 'PROPOSED' | 'APPROVED' | 'REJECTED';
  statusLabel: string;
  /** Zdanie od automatu: co policzył albo czego zabrakło. */
  statusNote: string | null;
  sourceFilename: string;
  sourceContentType: string;
  sourceSizeBytes: number | null;
  pageNumber: number;
  pageCount: number | null;
  rasterWidth: number | null;
  rasterHeight: number | null;
  renderDpi: number | null;
  cropX: number | null;
  cropY: number | null;
  cropW: number | null;
  cropH: number | null;
  cropSource: string | null;
  cropSourceLabel: string | null;
  cropConfidence: number | null;
  mapCrs: string | null;
  mapCrsLabel: string | null;
  crsSource: string | null;
  rmsM: number | null;
  /** false ⇒ punktów jest dokładnie tyle, ile niewiadomych: RMS = 0 z definicji, nie z jakości. */
  rmsMiarodajny: boolean;
  fitSource: string | null;
  skalaMPx: number | null;
  obrotDeg: number | null;
  skosDeg: number | null;
  anizotropia: number | null;
  bboxWgs84: [number, number, number, number] | null;
  layerCode: string | null;
  defaultOpacity: number | null;
  studyId: number | null;
  intakeId: number | null;
  assessmentId: number | null;
  approvedBy: string | null;
  approvedAt: string | null;
  createdAt: string;
  gcps: GeorefGcp[];
}

export interface GeorefCrsOption { code: string; label: string; }

export const georef = {
  list: (filtr?: { studyId?: number; intakeId?: number; assessmentId?: number }) => {
    const p = new URLSearchParams();
    if (filtr?.studyId) p.set('studyId', String(filtr.studyId));
    if (filtr?.intakeId) p.set('intakeId', String(filtr.intakeId));
    if (filtr?.assessmentId) p.set('assessmentId', String(filtr.assessmentId));
    const q = p.toString();
    return api.get<GeorefMap[]>('/api/georef' + (q ? `?${q}` : ''));
  },
  get: (id: number) => api.get<GeorefMap>(`/api/georef/${id}`),
  crsOptions: () => api.get<GeorefCrsOption[]>('/api/georef/crs-options'),
  /** Wgranie mapy — odpowiedź zawiera już propozycję automatu (wycięcie + punkty + transformacja). */
  upload: (file: File, opts: { name?: string; page?: number; studyId?: number; intakeId?: number; assessmentId?: number },
           onProgress?: (p: UploadProgress) => void): Promise<GeorefMap> => {
    const fd = new FormData();
    fd.append('file', file);
    if (opts.name) fd.append('name', opts.name);
    if (opts.page) fd.append('page', String(opts.page));
    if (opts.studyId) fd.append('studyId', String(opts.studyId));
    if (opts.intakeId) fd.append('intakeId', String(opts.intakeId));
    if (opts.assessmentId) fd.append('assessmentId', String(opts.assessmentId));
    return xhrUpload<GeorefMap>('/api/georef', fd, { onProgress });
  },
  analyze: (id: number, grid = true, objects = false) =>
    api.post<GeorefMap>(`/api/georef/${id}/analyze?grid=${grid}&objects=${objects}`),
  /** Poprawka istniejącej transformacji przez porównanie z podkładem (obraz-do-obrazu). */
  matchBasemap: (id: number) => api.post<GeorefMap>(`/api/georef/${id}/match-basemap`),
  setCrop: (id: number, crop: { x: number; y: number; w: number; h: number }) =>
    api.put<GeorefMap>(`/api/georef/${id}/crop`, crop),
  setPage: (id: number, page: number) => api.put<GeorefMap>(`/api/georef/${id}/page`, { page }),
  saveGcps: (id: number, gcps: Partial<GeorefGcp>[]) => api.put<GeorefMap>(`/api/georef/${id}/gcps`, gcps),
  setCrs: (id: number, crs: string) => api.put<GeorefMap>(`/api/georef/${id}/crs`, { crs }),
  approve: (id: number) => api.post<GeorefMap>(`/api/georef/${id}/approve`),
  reject: (id: number, reason?: string) => api.post<GeorefMap>(`/api/georef/${id}/reject`, { reason }),
  remove: (id: number) => api.del<void>(`/api/georef/${id}`),
  /** Obrys narysowany na dopasowanej mapie (piksele) → GeoJSON w WGS84. */
  traceToGeoJson: (id: number, points: number[][]) =>
    api.post<{ geoJson: string }>(`/api/georef/${id}/trace-to-geojson`, { points }),
  /** URL obrazu rastra roboczego (podgląd w edytorze). */
  rasterUrl: (id: number) => `/api/georef/${id}/raster`,
  /** Szablon kafelka podglądu dla mapy jeszcze niezatwierdzonej (MapLibre podstawia bbox). */
  previewTileUrl: (id: number) =>
    `/api/georef/${id}/preview-tile?BBOX={bbox-epsg-3857}&WIDTH=256&HEIGHT=256`,
};

// =============================================================================
// Lokalizacja GPS floty (V159) — protokół Atlas, dostawca Solid Security
// =============================================================================

/** Stan robót lokalizatora liczony po stronie serwera (jedno źródło prawdy dla mapy i legendy). */
export type GpsStatusCode = 'DRIVING' | 'IDLING' | 'PARKED' | 'STALE';

/** Skąd wzięło się powiązanie urządzenia z pojazdem. */
export type GpsMatchSource = 'MANUAL' | 'PLATE' | 'NONE';

export interface GpsVehiclePosition {
  deviceId: string;
  deviceName: string | null;
  vehicleId: number | null;
  vehicleLabel: string | null;
  registrationNumber: string | null;
  driverName: string | null;
  matchSource: GpsMatchSource;
  latitude: number;
  longitude: number;
  speedKmh: number;
  headingDeg: number;
  /** null = urządzenie nie raportuje stanu zapłonu. */
  ignitionOn: boolean | null;
  /** ISO-8601; null = dostawca nie podał czasu pomiaru. */
  fixedAt: string | null;
  ageMinutes: number | null;
  stale: boolean;
  status: GpsStatusCode;
  statusName: string;
  color: string;
  /** Podpis punktu — nazwa pojazdu, a gdy brak powiązania: nazwa urządzenia u dostawcy. */
  displayName: string;
}

export interface GpsIntegrationStatus {
  /** false = integracja wyłączona; warstwa GPS nie ma się w ogóle pojawić. */
  enabled: boolean;
  /** false = brak kompletu poświadczeń (administrator ich jeszcze nie wprowadził). */
  configured: boolean;
  mode: string;
  /** Co ile sekund odpytywać /positions. 0 = bez automatycznego odświeżania. */
  refreshSeconds: number;
  staleMinutes: number;
}

export interface GpsDeviceOption {
  deviceId: string;
  deviceName: string | null;
  assignedVehicleId: number | null;
  assignedVehicleLabel: string | null;
}

export const gps = {
  status: () => api.get<GpsIntegrationStatus>('/api/gps/status'),
  positions: () => api.get<GpsVehiclePosition[]>('/api/gps/positions'),
  /** Urządzenia u dostawcy — lista wyboru w karcie pojazdu (wymaga FLEET_MANAGE). */
  devices: () => api.get<GpsDeviceOption[]>('/api/gps/devices'),
};
